/* ==========================================================================
   RW Returns Portal — client-side state machine
   Screens: lookup (fallback entry) → start → carrier → [collection] → labels
   Drives the /functions/v1/returns-portal edge function endpoints.
   ========================================================================== */

(function () {
  "use strict";

  const root = document.getElementById("rw-rp-root");
  if (!root) return;
  const stage = document.getElementById("rw-rp-stage");
  const EDGE_FN_URL = root.dataset.edgeFnUrl;
  const SUPPORT_EMAIL = root.dataset.supportEmail;

  // Mock mode: ?mock=1 intercepts Zigzag-dependent endpoints with stub data
  // so the full flow is clickable before Zigzag creds land.
  // ?mock=partial simulates a partial booking (3 of 4 bags booked) for UI preview.
  const MOCK_MODE = new URLSearchParams(location.search).get("mock");
  const MOCK = MOCK_MODE === "1" || MOCK_MODE === "partial";
  const MOCK_PARTIAL = MOCK_MODE === "partial";
  if (MOCK) {
    const banner = document.createElement("div");
    banner.style.cssText = "background:#c9a96e;color:#1A1713;padding:8px 16px;font:500 12px/1.4 'Cabinet Grotesk',sans-serif;letter-spacing:.08em;text-transform:uppercase;text-align:center;";
    banner.textContent = MOCK_PARTIAL
      ? "Partial mock mode — simulates 3 of 4 bags booked. No real Zigzag calls."
      : "Mock mode — no real Zigzag calls. Labels and tracking are placeholders.";
    document.body.prepend(banner);
  }

  /* ------------------------------ state ------------------------------ */
  const state = {
    token: null,           // signed JWT from /auth/token or /auth/lookup
    order: null,           // { order_number, shopify_order_id, email, bags_ordered, shipping_address, customer_name }
    returnId: null,        // preview UUID before commit; first real Zigzag returnId after commit (kept for cancel-collection)
    returnIds: [],         // ALL real Zigzag returnIds — one per bag (length === bagCount after commit)
    carriers: [],          // CarrierService[] fetched on /returns/start
    selectedCarrier: null, // one of state.carriers
    bagCount: null,
    serviceType: null,     // 'personal' | 'stylist' | 'business' (auto = stylist when JWT carries stylist_id)
    clientId: null,        // selected client's UUID (null when personal or before pick)
    clientName: null,      // derived for display: "First Last"
    clientList: null,      // cached list of this stylist's clients ({id, first_name, last_name, email})
    pooledTotal: null,     // pooled remaining bag count across all orders (stylist session only)
    pooledOrders: null,    // [{order_number, remaining, month}] (stylist session only)
    collection: null,      // { date, address }
    labelData: null,       // [{return_id, status, pdf_base64, tracking}] — one per bag
  };

  /* ------------------------------ helpers ------------------------------ */
  async function api(method, path, body, authToken) {
    if (MOCK) {
      const mock = mockResponse(method, path, body);
      if (mock !== undefined) {
        await new Promise((r) => setTimeout(r, 350)); // mimic network
        return mock;
      }
    }
    const headers = { "content-type": "application/json" };
    const tok = authToken ?? state.token;
    if (tok) headers.authorization = "Bearer " + tok;
    const opts = { method, headers };
    if (body !== undefined) opts.body = JSON.stringify(body);
    const res = await fetch(EDGE_FN_URL + path, opts);
    const parsed = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(parsed.error || ("HTTP " + res.status));
    return parsed;
  }

  /* ------------------------------ mock fixtures ------------------------------ */
  function mockResponse(method, path, body) {
    // /auth/lookup — accept any credentials, issue a fake token
    if (method === "POST" && path === "/auth/lookup") {
      return { token: "mock-token" };
    }
    // /auth/token — mint fake
    if (method === "POST" && path === "/auth/token") {
      return { token: "mock-token" };
    }
    // /auth/request-link — always succeeds silently (anti-enumeration; SPA ignores body)
    if (method === "POST" && path === "/auth/request-link") {
      return { ok: true };
    }
    // /order — fake Benjamin's order
    if (method === "GET" && path === "/order") {
      return {
        order_number: "blado-byrnes-240426-5",
        shopify_order_id: 12978068095359,
        email: "bladobyrnes@hotmail.com",
        bags_ordered: 5,
        shipping_address: {
          first_name: "Benjamin", last_name: "Lado-Byrnes",
          address1: "Ridgemount", city: "Weybridge", zip: "KT13 9JD",
          phone: "+447805796090",
        },
        customer_name: "Benjamin Lado-Byrnes",
      };
    }
    // /returns/start — mock return ID + 5 carriers (with collection dates for Royal Mail)
    if (method === "POST" && path === "/returns/start") {
      const today = new Date();
      const days = [];
      for (let i = 1; i <= 6 && days.length < 6; i++) {
        const d = new Date(today);
        d.setDate(today.getDate() + i);
        const dow = d.getDay();
        if (dow !== 0 && dow !== 6) days.push(d.toISOString().slice(0, 10));
      }
      return {
        return_id: "MOCK-" + Date.now(),
        carriers: [
          { id: "rm-collect", carrierName: "Royal Mail", serviceName: "Parcel Collect Tracked Returns 24",
            serviceType: "Collection from Home", printerRequired: false, isFree: true, availableCollectionDates: days },
          { id: "rm-drop", carrierName: "Royal Mail", serviceName: "Tracked Returns 24 Labels to Go",
            serviceType: "Drop off at Post Office", printerRequired: false, isFree: true },
          { id: "dpd-shop", carrierName: "DPD", serviceName: "Return to Shop",
            serviceType: "Drop off at Parcel Shop", printerRequired: false, isFree: true },
          { id: "inpost-locker", carrierName: "InPost", serviceName: "Tracked Returns (QR)",
            serviceType: "Return to parcel locker", printerRequired: false, isFree: true },
          { id: "yodel-shop", carrierName: "Yodel", serviceName: "Print In Store",
            serviceType: "Drop off at Parcel Shop", printerRequired: false, isFree: true },
        ],
      };
    }
    // /returns/:id/carrier — return one fake return_id per bag
    // ?mock=partial: simulate 3 of 4 bags booked (partial-success fixture)
    if (method === "POST" && /^\/returns\/[^/]+\/carrier$/.test(path)) {
      if (MOCK_PARTIAL) {
        return { ok: true, return_ids: ["ret-1", "ret-2", "ret-3"], booked_count: 3, failed: [{ bagIndex: 3, reason: "lag" }], total: 4 };
      }
      const bags = (body && body.bag_count) || state.bagCount || 1;
      const ids = [];
      for (let i = 0; i < bags; i++) ids.push("MOCK-" + Date.now() + "-" + i);
      return { ok: true, return_ids: ids };
    }
    // /returns/:id/labels — fake ready payload
    if (method === "GET" && /^\/returns\/[^/]+\/labels$/.test(path)) {
      // Minimal valid base64 PDF ("Mock Label").
      const MOCK_PDF_B64 =
        "JVBERi0xLjQKJeLjz9MKMyAwIG9iago8PC9UeXBlL1BhZ2UvUGFyZW50IDEgMCBSL01lZGlhQm94WzAgMCA0MDAgMjAwXS9SZXNvdXJjZXM8PC9Gb250PDwvRjEgMiAwIFI+Pj4+L0NvbnRlbnRzIDQgMCBSPj4KZW5kb2JqCjQgMCBvYmoKPDwvTGVuZ3RoIDQ0Pj5zdHJlYW0KQlQgL0YxIDI0IFRmIDUwIDEwMCBUZCAoTW9jayBMYWJlbCAtIFJXKSBUaiBFVAplbmRzdHJlYW0KZW5kb2JqCjIgMCBvYmoKPDwvVHlwZS9Gb250L1N1YnR5cGUvVHlwZTEvQmFzZUZvbnQvSGVsdmV0aWNhPj4KZW5kb2JqCjEgMCBvYmoKPDwvVHlwZS9QYWdlcy9LaWRzWzMgMCBSXS9Db3VudCAxPj4KZW5kb2JqCjUgMCBvYmoKPDwvVHlwZS9DYXRhbG9nL1BhZ2VzIDEgMCBSPj4KZW5kb2JqCnhyZWYKMCA2CjAwMDAwMDAwMDAgNjU1MzUgZiAKMDAwMDAwMDMxMiAwMDAwMCBuIAowMDAwMDAwMjQ5IDAwMDAwIG4gCjAwMDAwMDAwMTUgMDAwMDAgbiAKMDAwMDAwMDE0NSAwMDAwMCBuIAowMDAwMDAwMzYzIDAwMDAwIG4gCnRyYWlsZXIKPDwvU2l6ZSA2L1Jvb3QgNSAwIFI+PgpzdGFydHhyZWYKNDEwCiUlRU9G";
      const tracking = [];
      const bags = state.bagCount || 1;
      for (let i = 0; i < bags; i++) {
        const id = "155019591985" + String(10 + i).padStart(2, "0");
        tracking.push({
          date: new Date().toISOString(),
          status: "Label was generated",
          description: "Label was generated via " + (state.selectedCarrier && state.selectedCarrier.carrierName),
          code: 10,
          carrier_tracking_url: null,
          carrier_tracking_id: id,
        });
      }
      return { status: "ready", pdf_base64: MOCK_PDF_B64, delivery_instructions: null, tracking };
    }
    // /returns/:id/send-to-client, /resend-email — always ok
    if (method === "POST" && /^\/returns\/[^/]+\/(send-to-client|resend-email)$/.test(path)) {
      return { ok: true };
    }
    // /postcode-lookup — fake validation
    if (method === "POST" && path === "/postcode-lookup") {
      const pc = body && body.postcode;
      if (!pc || pc.length < 5) throw new Error("invalid postcode");
      return { postcode: pc, admin_district: "Elmbridge", region: "South East", country: "England", latitude: 51.38, longitude: -0.43 };
    }
    // /clients — list this stylist's registered clients (mock)
    if (method === "GET" && path === "/clients") {
      return {
        clients: [
          { id: "mock-c1", first_name: "Chloe", last_name: "Anderson", email: "chloe@example.com" },
          { id: "mock-c2", first_name: "Marie", last_name: "Bisset", email: "marie@example.com" },
          { id: "mock-c3", first_name: "Olivia", last_name: "Chen", email: "olivia@example.com" },
        ],
      };
    }
    // /clients/register — register new client inline (mock)
    if (method === "POST" && path === "/clients/register") {
      const b = body || {};
      if (!b.first_name || !b.last_name || !b.email) throw new Error("name and email required");
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(b.email)) throw new Error("invalid email");
      return {
        client: {
          id: "mock-new-" + Date.now(),
          first_name: b.first_name,
          last_name: b.last_name,
          email: b.email,
        },
      };
    }
    return undefined; // fall through to real network
  }

  function h(tag, attrs, children) {
    const el = document.createElement(tag);
    if (attrs) {
      for (const [k, v] of Object.entries(attrs)) {
        if (k === "class") el.className = v;
        else if (k === "onClick") el.addEventListener("click", v);
        else if (k === "onInput") el.addEventListener("input", v);
        else if (k === "onChange") el.addEventListener("change", v);
        else if (k === "html") el.innerHTML = v;
        else if (v === true) el.setAttribute(k, "");
        else if (v !== false && v != null) el.setAttribute(k, v);
      }
    }
    if (children) {
      for (const c of [].concat(children)) {
        if (c == null || c === false) continue;
        el.appendChild(typeof c === "string" ? document.createTextNode(c) : c);
      }
    }
    return el;
  }

  function renderShell({ step, smallCaps, h1, lead }) {
    const pills = [0, 1, 2].map((i) =>
      h("span", { class: "rw-rp-pill" + (i < step ? " active" : "") })
    );
    const hero = h("section", { class: "rw-rp-hero" }, [
      h("div", { class: "rw-rp-hero-inner" }, [
        h("div", { class: "rw-rp-progress" }, pills),
        smallCaps && h("span", { class: "rw-rp-small-caps" }, smallCaps),
        h1 && h("h1", { class: "rw-rp-h1" }, h1),
        lead && h("p", { class: "rw-rp-lead", html: lead }),
      ]),
    ]);
    const content = h("section", { class: "rw-rp-content" }, [
      h("div", { class: "rw-rp-content-inner", id: "rw-rp-body" }),
    ]);
    stage.innerHTML = "";
    stage.appendChild(hero);
    stage.appendChild(content);
    return content.querySelector("#rw-rp-body");
  }

  function showInlineError(containerOrMsg, msg) {
    if (typeof containerOrMsg === "string") {
      // single-arg form: inject a warning note at the top of the current body
      const target = document.getElementById("rw-rp-body") || root;
      const existing = target.querySelector(".rw-rp-note-warn");
      if (existing) existing.remove();
      target.prepend(h("div", { class: "rw-rp-note rw-rp-note-warn" }, containerOrMsg));
      return;
    }
    const container = containerOrMsg;
    const existing = container.querySelector(".rw-rp-error");
    if (existing) existing.remove();
    container.prepend(h("div", { class: "rw-rp-error" }, msg));
  }

  // Transitional loading screen between bag-count submit and the carriers screen.
  // The Shopify-verify + Zigzag-upsert + Zigzag-create + carriers chain takes ~3s,
  // so we replace the disabled button with a full-screen loading state.
  function renderTransitionLoading({ step, smallCaps, h1, lead }) {
    const body = renderShell({ step, smallCaps, h1, lead });
    const wrap = h("div", { class: "rw-rp-loading" }, [
      h("div", { class: "rw-rp-spinner" }),
      h("p", { class: "rw-rp-loading-msg", id: "rw-rp-loading-msg" }, "Setting things up with the carrier…"),
    ]);
    body.appendChild(wrap);
  }

  function setLoadingMsg(msg) {
    const el = document.getElementById("rw-rp-loading-msg");
    if (el) el.textContent = msg;
  }

  // Sanity ceiling on a bag count, not a capacity limit — real clearouts run
  // to 40 bags. Shortfalls are covered by an automatic pool top-up server-side.
  const BAG_COUNT_CEILING = 50;

  /* ------------------------------ fetchPooledCapacity ------------------------------ */
  // Fetches GET /stylist/capacity and stores the result in state.
  // Called only on the stylist session path — customers skip this entirely.
  // Internally try/caught so a network failure is non-fatal; renderStart falls
  // back gracefully when state.pooledTotal is null.
  async function fetchPooledCapacity() {
    const claims = decodeJwt(state.token);
    if (!(claims && claims.stylist_id)) return; // customer session — nothing to fetch
    try {
      const resp = await api("GET", "/stylist/capacity");
      state.pooledTotal = typeof resp.total === "number" ? resp.total : 0;
      state.pooledOrders = Array.isArray(resp.orders) ? resp.orders : [];
    } catch (_) {
      // Non-fatal: leave pooledTotal null; renderStart will treat null as 0.
      state.pooledTotal = null;
      state.pooledOrders = null;
    }
  }

  /* ------------------------------ boot ------------------------------ */
  async function boot() {
    if (MOCK) {
      // Skip lookup — land straight on Screen 1 with the fake order.
      state.token = "mock-token";
      state.order = await api("GET", "/order");
      return renderStart();
    }
    const params = new URLSearchParams(location.search);

    // ── Stylist session layer ────────────────────────────────────────────────
    // Entirely additive: runs before the customer ?t= branch and never
    // touches it.  The customer path below is byte-identical to the
    // original; it is only reached when ?st= is absent and sessionStorage
    // holds no valid stylist session.
    // NOTE: the stylist session lives in sessionStorage (not localStorage) so
    // a stylist logs in fresh each browsing session but stays signed in
    // within it (e.g. portal -> Book a Return keeps working without re-auth).
    const st = params.get("st");
    if (st) {
      // Persist, then scrub the token from the address bar so it is never
      // exposed on a page refresh or in the browser history entry.
      // I1: guard setItem — storage may be blocked in privacy mode / webviews.
      try { sessionStorage.setItem("rw_stylist_session", st); } catch (_) {}
      const cleanParams = new URLSearchParams(location.search);
      cleanParams.delete("st");
      const cleanSearch = cleanParams.toString() ? "?" + cleanParams.toString() : "";
      history.replaceState({}, "", location.pathname + cleanSearch);
      state.token = st;
      try {
        const [order] = await Promise.all([api("GET", "/order"), fetchPooledCapacity()]);
        state.order = order;
        return renderStart();
      } catch (_) {
        // I1: guard removeItem.
        try { sessionStorage.removeItem("rw_stylist_session"); } catch (_e) {}
        return renderStylistSignIn();
      }
    }
    // I2: if an explicit ?t= magic link is present it must win over any stored
    // stylist session, so a dual-role stylist's personal link is never shadowed.
    // Only read storage when no ?t= is in the URL.
    let storedSession = null;
    if (!params.get("t")) {
      // I1: guard getItem — treat any throw as "no stored session".
      try { storedSession = sessionStorage.getItem("rw_stylist_session"); } catch (_) {}
    }
    // M2: track whether a stylist session was present but expired/invalid so
    // we can route to sign-in rather than the customer lookup screen.
    let hadStylistSession = false;
    if (storedSession) {
      const claims = decodeJwt(storedSession);
      if (claims && typeof claims.exp === "number" && claims.exp * 1000 > Date.now()) {
        state.token = storedSession;
        try {
          const [order] = await Promise.all([api("GET", "/order"), fetchPooledCapacity()]);
          state.order = order;
          return renderStart();
        } catch (_) {
          // I1: guard removeItem.
          try { sessionStorage.removeItem("rw_stylist_session"); } catch (_e) {}
          return renderStylistSignIn();
        }
      } else {
        // I1: guard removeItem.
        try { sessionStorage.removeItem("rw_stylist_session"); } catch (_e) {}
        // M2: expired or invalid — remember a stylist session existed so the
        // final route below shows sign-in rather than the customer lookup.
        hadStylistSession = true;
      }
    }
    // ── End stylist session layer ────────────────────────────────────────────

    // Customer ?t= path — UNCHANGED from original.
    const t = params.get("t");
    if (t) {
      state.token = t;
      try {
        state.order = await api("GET", "/order");
        return renderStart();
      } catch (e) {
        return renderLookup("Link expired or invalid. Enter your order details to continue.");
      }
    }

    // No token of any kind.
    // Discriminator: ?stylist=1 or a previously stored (now expired/invalid)
    // stylist session routes to sign-in.  All other entries (customers without
    // a ?t= link, direct URL visits) see the order+email lookup — the same
    // screen shown before this feature existed.  No customer is ever routed to
    // the stylist auth flow unless they arrived via ?stylist=1 or had a session.
    if (params.get("stylist") === "1" || hadStylistSession) {
      return renderStylistSignIn();
    }
    return renderLookup();
  }

  /* ------------------------------ Screen: lookup ------------------------------ */
  function renderLookup(errorMsg) {
    const body = renderShell({
      step: 1,
      smallCaps: "Start",
      h1: "Find your order",
      lead: "Enter the details from your order confirmation. Or check your inbox for the email we sent when your bags arrived — it'll take you straight in.",
    });
    const card = h("div", { class: "rw-rp-card" });
    if (errorMsg) card.appendChild(h("div", { class: "rw-rp-error" }, errorMsg));
    card.appendChild(h("label", { class: "rw-rp-field-label", for: "rp-order" }, "Order number"));
    const orderInput = h("input", { class: "rw-rp-input", id: "rp-order", placeholder: "e.g. RW-1080" });
    card.appendChild(orderInput);
    card.appendChild(h("label", { class: "rw-rp-field-label", for: "rp-email", style: "margin-top:16px" }, "Email on the order"));
    const emailInput = h("input", { class: "rw-rp-input", id: "rp-email", type: "email", placeholder: "you@example.com" });
    card.appendChild(emailInput);
    // Pre-fill from URL params if the visitor came through the stylist portal
    // (which forwards `?o=<order>&e=<email>`). JWT cookie takes over after first
    // successful login so this only matters the first time.
    try {
      const qp = new URLSearchParams(window.location.search);
      const oParam = (qp.get("o") || "").trim();
      const eParam = (qp.get("e") || "").trim();
      if (oParam) orderInput.value = oParam;
      if (eParam) emailInput.value = eParam;
    } catch (_) { /* non-fatal */ }
    const submit = h("button", { class: "rw-rp-btn", style: "margin-top:24px" }, "Continue");
    submit.addEventListener("click", async () => {
      const order_number = orderInput.value.trim();
      const email = emailInput.value.trim();
      if (!order_number || !email) return;
      submit.disabled = true;
      submit.textContent = "Checking…";
      try {
        const { token } = await api("POST", "/auth/lookup", { order_number, email });
        state.token = token;
        // fetchPooledCapacity, like the magic-link and stored-session paths
        // above. Without it a STYLIST signing in with their order number and
        // email — the route the "Find your order" screen actually advertises —
        // reached renderStart with pooledTotal still null, which renders
        // "We couldn't load your bags just now. Please refresh and try again."
        // Refreshing runs the same path and fails the same way, so the stylist
        // could not book a return at all. It is a no-op on a customer session:
        // the function returns immediately when the token carries no stylist_id.
        const [order] = await Promise.all([api("GET", "/order"), fetchPooledCapacity()]);
        state.order = order;
        renderStart();
      } catch (e) {
        submit.disabled = false;
        submit.textContent = "Continue";
        showInlineError(card, e.message);
      }
    });
    card.appendChild(submit);
    body.appendChild(card);
  }

  /* ------------------------------ Screen: stylist sign-in ------------------------------ */
  // First-visit-only screen for stylists.  Customers never reach this screen
  // unless they deliberately navigate to ?stylist=1 without a ?t= token.
  // An escape link on the form sends them to the order+email lookup instead.
  function renderStylistSignIn() {
    const body = renderShell({
      step: 0,
      smallCaps: "Sign in",
      h1: "Let's get you signed in.",
      lead: "Enter your email and we'll send you a secure link to sign in. You'll only need to do this the first time.",
    });
    const card = h("div", { class: "rw-rp-card" });
    card.appendChild(h("label", { class: "rw-rp-field-label", for: "rp-stylist-email" }, "Your email"));
    const emailInput = h("input", {
      class: "rw-rp-input",
      id: "rp-stylist-email",
      type: "email",
      placeholder: "you@example.com",
      autocomplete: "email",
    });
    card.appendChild(emailInput);
    const submit = h("button", { class: "rw-rp-btn", style: "margin-top:24px" }, "Email me my link");
    submit.addEventListener("click", async () => {
      const email = emailInput.value.trim();
      if (!email) return;
      submit.disabled = true;
      submit.textContent = "Sending…";
      try {
        await api("POST", "/auth/request-link", { email });
      } catch (_) {
        // Response is intentionally uniform (anti-enumeration).
        // Always show the check-email screen regardless.
      }
      renderCheckEmail(email);
    });
    card.appendChild(submit);
    // Escape hatch: customers who arrive at the page without a ?t= magic link
    // can still reach the order+email lookup from here.
    card.appendChild(
      h("p", { style: "margin-top:20px;text-align:center;font-size:13px;color:var(--rp-muted);line-height:1.5" }, [
        "Returning a personal order? ",
        h("button", {
          class: "rw-rp-text-link",
          style: "font-size:13px;padding:0",
          onClick(e) { e.preventDefault(); renderLookup(); },
        }, "Look it up here"),
        ".",
      ])
    );
    body.appendChild(card);
  }

  /* ------------------------------ Screen: check email ------------------------------ */
  // Shown after "Email me my link" is tapped.  Stays on this screen whether
  // the server accepted or rejected the address (anti-enumeration).
  function renderCheckEmail(email) {
    const body = renderShell({
      step: 0,
      smallCaps: "Sign in",
      h1: "Check your email.",
      lead: null,
    });
    const card = h("div", { class: "rw-rp-card" });
    // M1: render the email as a text node inside <strong> — never interpolate
    // user-supplied values into innerHTML.
    card.appendChild(
      h("div", { class: "rw-rp-confirm-strip" }, [
        h("div", {}, [
          "We've sent a secure link to ",
          h("strong", {}, email),
          ". Tap it once and you'll be signed in.",
        ]),
      ])
    );
    const resendBtn = h("button", {
      class: "rw-rp-text-link",
      style: "font-size:13.5px;padding:0",
    }, "send it again");
    resendBtn.addEventListener("click", async () => {
      resendBtn.disabled = true;
      try {
        await api("POST", "/auth/request-link", { email });
      } catch (_) { /* silent — same as first send */ }
      resendBtn.disabled = false;
    });
    card.appendChild(
      h("p", { style: "margin-top:20px;font-size:13.5px;color:var(--rp-muted);line-height:1.6" }, [
        "Not there in a minute? Have a look in your spam folder, or ",
        resendBtn,
        ".",
      ])
    );
    body.appendChild(card);
  }

  /* ------------------------------ Screen: start ------------------------------ */
  // Refresh state.order from the backend, then render. Use for back-button +
  // "return more bags" so the bag count reflects what was just consumed
  // (the /order endpoint also runs an on-demand Zigzag poll first).
  async function renderStartFresh() {
    renderTransitionLoading({
      step: 1, smallCaps: "Start",
      h1: "Refreshing your order…",
      lead: "Pulling the latest bag count.",
    });
    try {
      state.order = await api("GET", "/order");
      await fetchPooledCapacity();
    } catch (e) {
      // Fall through to render with whatever's cached — user can still proceed.
      console.error("renderStartFresh refetch failed:", e && e.message);
    }
    renderStart();
  }

  function renderStart() {
    const order = state.order;
    const claims = decodeJwt(state.token);
    const isStylist = !!(claims && claims.stylist_id);

    // bags_remaining is computed server-side from the returns table:
    //   ordered − sum(bag_count of non-terminal returns).
    // Falls back to bags_ordered for older API responses that don't include it.
    const remaining = (typeof order.bags_remaining === "number")
      ? order.bags_remaining
      : order.bags_ordered;

    // Stylist sessions use the pooled total across all orders (fetched before
    // this render).  Null means the fetch failed — we distinguish that case
    // from a genuine 0 (no bags out) to show a retryable error rather than
    // falsely claiming the stylist has no bags.
    const pooledTotal = isStylist ? state.pooledTotal : 0;

    // The effective stepper cap and default differ by session type:
    //   Stylist  → any number up to the sanity ceiling; default = 1
    //   Customer → cap = per-order remaining;            default = remaining
    //
    // A stylist is no longer capped at their pooled total. If they say they
    // have more bags than the pool knows about, the server creates the extra
    // capacity and the booking carries on — the bags are free, and refusing
    // over a counting discrepancy just stalls a clearout that is already
    // packed. BAG_COUNT_CEILING only catches a mistyped number.
    const stepMax = isStylist ? BAG_COUNT_CEILING : remaining;
    const stepDefault = isStylist ? 1 : remaining;

    const firstName = (order.customer_name || "").split(" ")[0] || "";
    const body = renderShell({
      step: 1,
      smallCaps: "Start",
      h1: firstName ? "Send in your bags, " + firstName + "." : "Send in your bags.",
      lead: "Tell us a little about this return — we'll take it from there.",
    });
    const card = h("div", { class: "rw-rp-card" });

    // Hard stop — capacity fetch failed; show retryable error before any recap.
    // Must come first so we never render "null bags" in the stylist recap below.
    if (isStylist && pooledTotal === null) {
      card.appendChild(
        h("div", { class: "rw-rp-field-hint", style: "margin-top:18px;" },
          "We couldn't load your bags just now. Please refresh the page and try again."),
      );
      body.appendChild(card);
      return;
    }

    // Recap — stylist sees pooled total; customer path is byte-identical to before.
    if (isStylist) {
      card.appendChild(
        h("div", { class: "rw-rp-recap" }, [
          h("span", { class: "rw-rp-recap-label" }, "Your bags"),
          h("strong", null, pooledTotal + " bag" + (pooledTotal === 1 ? "" : "s")),
          " available to return",
        ]),
      );
    } else {
      card.appendChild(
        h("div", { class: "rw-rp-recap" }, [
          h("span", { class: "rw-rp-recap-label" }, "Your order"),
          h("strong", null, order.order_number),
          h("br"),
          order.bags_ordered + " bag" + (order.bags_ordered === 1 ? "" : "s") + " ordered · " + remaining + " remaining",
        ]),
      );
    }

    // Hard stop — two remaining cases:
    //   1. Stylist: fetch succeeded but genuinely 0 bags out → friendly empty msg.
    //   2. Customer: nothing remaining → all-accounted-for msg.
    if (stepMax <= 0) {
      if (isStylist) {
        // Unreachable now that the stylist ceiling is a constant, but left in
        // place: if the ceiling is ever tied back to real capacity this is the
        // message that should show.
        card.appendChild(
          h("div", { class: "rw-rp-field-hint", style: "margin-top:18px;" },
            "You have no bags to return right now. Once you have bags out with clients, they'll show here."),
        );
      } else {
        card.appendChild(
          h("div", { class: "rw-rp-field-hint", style: "margin-top:18px;" },
            "All your bags are accounted for. If you need to send more, message us at hello@rewardedwardrobe.co.uk."),
        );
      }
      body.appendChild(card);
      return;
    }

    // bag count stepper
    const bagField = h("div", { class: "rw-rp-field" });
    bagField.appendChild(h("label", { class: "rw-rp-field-label" }, "How many filled bags are you sending today?"));
    bagField.appendChild(h("div", { class: "rw-rp-field-hint" }, "Just the ones you've packed — we'll generate one label per bag."));
    const stepper = h("div", { class: "rw-rp-stepper" });
    const minus = h("button", { type: "button" }, "–");
    const bagInput = h("input", { value: String(stepDefault), readonly: true });
    const plus = h("button", { type: "button" }, "+");
    stepper.appendChild(minus);
    stepper.appendChild(bagInput);
    stepper.appendChild(plus);
    bagField.appendChild(stepper);
    minus.addEventListener("click", () => {
      bagInput.value = String(Math.max(1, parseInt(bagInput.value, 10) - 1));
      minus.disabled = parseInt(bagInput.value, 10) <= 1;
      plus.disabled = false;
    });
    plus.addEventListener("click", () => {
      bagInput.value = String(Math.min(stepMax, parseInt(bagInput.value, 10) + 1));
      plus.disabled = parseInt(bagInput.value, 10) >= stepMax;
      minus.disabled = false;
    });
    plus.disabled = isStylist ? (stepDefault >= stepMax) : (remaining <= 1);
    card.appendChild(bagField);

    // service type — same dropdown for everyone. Pre-select "Stylist clearout"
    // when the JWT carries a stylist_id claim so partners can keep going
    // without picking, but they can still switch to Personal if they're
    // doing their own clearout (or to Business for that edge case).
    const isPartner = isStylist;
    const svcField = h("div", { class: "rw-rp-field" });
    svcField.appendChild(h("label", { class: "rw-rp-field-label" }, "What kind of clearout is this?"));
    const select = h("select", { class: "rw-rp-select" }, [
      h("option", { value: "", disabled: true }, "Please choose…"),
      h("option", { value: "personal" }, "Personal clearout"),
      h("option", { value: "stylist" }, "Stylist / organiser clearout"),
      h("option", { value: "business" }, "Business clearout"),
    ]);
    select.value = isPartner ? "stylist" : "";
    svcField.appendChild(select);
    card.appendChild(svcField);

    // conditional client picker — dropdown of THIS stylist's registered clients.
    // Sits BETWEEN service type and reason so the flow reads:
    //   what kind? → which client? → what's prompting?
    const clientField = h("div", { class: "rw-rp-field", style: "display:none" });
    clientField.appendChild(h("label", { class: "rw-rp-field-label" }, "Which client is this for?"));
    const clientSelect = h("select", { class: "rw-rp-select" }, [
      h("option", { value: "", selected: true, disabled: true }, "Loading…"),
    ]);
    clientField.appendChild(clientSelect);
    clientField.appendChild(h("div", { class: "rw-rp-field-hint" }, "If they're not in the list, pick \"Add new client\" — we'll register them and email them the next steps."));

    // inline new-client mini-form (revealed when "Add new client" is picked)
    const addClientForm = h("div", { class: "rw-rp-add-client", style: "display:none;margin-top:12px;padding:14px;background:#FAF7F1;border:1px solid #EDE3CF;border-radius:4px;" });
    addClientForm.appendChild(h("div", { class: "rw-rp-field-label", style: "margin-bottom:8px;" }, "Register a new client"));
    const newFirst = h("input", { class: "rw-rp-input", placeholder: "First name", style: "margin-bottom:6px;" });
    const newLast = h("input", { class: "rw-rp-input", placeholder: "Last name", style: "margin-bottom:6px;" });
    const newEmail = h("input", { class: "rw-rp-input", type: "email", placeholder: "Email", style: "margin-bottom:8px;" });
    const registerBtn = h("button", { class: "rw-rp-btn", style: "background:#1A1713;color:#FAF7F1;" }, "Register & select");
    const newClientStatus = h("div", { class: "rw-rp-field-hint", style: "margin-top:6px;color:#8a2e2e;display:none;" });
    addClientForm.appendChild(newFirst);
    addClientForm.appendChild(newLast);
    addClientForm.appendChild(newEmail);
    addClientForm.appendChild(registerBtn);
    addClientForm.appendChild(newClientStatus);
    clientField.appendChild(addClientForm);

    // Soft-delete control — appears once a real client is picked. Lets the
    // stylist remove a wrongly-added or no-longer-active client. Customer
    // record + history stay intact server-side (referring_stylist_id → null).
    const removeRow = h("div", { class: "rw-rp-remove-client", style: "display:none;margin-top:10px;font-size:12px;" });
    const removeLink = h("a", { href: "#", style: "color:#847968;text-decoration:underline;cursor:pointer;" }, "Remove this client from my list");
    const removeStatus = h("span", { style: "display:none;margin-left:8px;color:#847968;" });
    removeRow.appendChild(removeLink);
    removeRow.appendChild(removeStatus);
    clientField.appendChild(removeRow);

    card.appendChild(clientField);

    const cont = h("button", { class: "rw-rp-btn" }, "Continue");
    cont.disabled = true;
    card.appendChild(cont);

    function clientLabel(c) {
      var nm = ((c.first_name || "") + " " + (c.last_name || "")).trim();
      return nm + (c.email ? " — " + c.email : "");
    }
    function rebuildClientOptions() {
      clientSelect.innerHTML = "";
      clientSelect.appendChild(h("option", { value: "", selected: true, disabled: true }, "Please choose…"));
      (state.clientList || []).forEach(function (c) {
        clientSelect.appendChild(h("option", { value: c.id }, clientLabel(c)));
      });
      clientSelect.appendChild(h("option", { value: "__add__" }, "+ Add new client"));
    }
    async function loadClients() {
      try {
        const resp = await api("GET", "/clients");
        state.clientList = resp.clients || [];
        rebuildClientOptions();
      } catch (e) {
        clientSelect.innerHTML = "";
        clientSelect.appendChild(h("option", { value: "", selected: true, disabled: true }, "Couldn't load clients — try again later"));
      }
    }

    // Track whether the dropdown has been populated with real options yet
    // for THIS render of the start screen. Re-rendering doesn't reset
    // state.clientList (cached on `state`), so we use a local flag.
    let optionsHydrated = false;
    const recheck = () => {
      const svc = select.value;
      const isStylist = svc === "stylist" || svc === "business";
      clientField.style.display = isStylist ? "" : "none";
      // First time we hit the stylist branch on THIS render: hydrate the
      // dropdown. Either rebuild from cached list, or fetch fresh.
      // Subsequent change events skip this — otherwise clicking an option
      // would re-render the dropdown mid-click and lose the selection.
      if (isStylist && !optionsHydrated) {
        optionsHydrated = true;
        if (state.clientList) rebuildClientOptions();
        else loadClients();
      }
      const sv = clientSelect.value;
      const validClientPicked = sv && sv !== "__add__";
      const clientOk = svc === "personal" || validClientPicked;
      const ok = svc && clientOk;
      cont.disabled = !ok;
    };
    select.addEventListener("change", recheck);
    // Partner path: pre-selected to "stylist", kick the client picker into
    // view immediately + start loading the roster.
    if (isPartner) recheck();
    clientSelect.addEventListener("change", () => {
      addClientForm.style.display = clientSelect.value === "__add__" ? "" : "none";
      const sv = clientSelect.value;
      const validClientPicked = sv && sv !== "__add__";
      removeRow.style.display = validClientPicked ? "" : "none";
      removeStatus.style.display = "none";
      recheck();
    });

    removeLink.addEventListener("click", async (ev) => {
      ev.preventDefault();
      const id = clientSelect.value;
      if (!id || id === "__add__") return;
      const picked = (state.clientList || []).find((c) => c.id === id);
      const name = picked ? ((picked.first_name || "") + " " + (picked.last_name || "")).trim() : "this client";
      if (!confirm("Remove " + name + " from your list?\n\nTheir record + any past returns are kept — they just won't appear in this dropdown anymore.")) {
        return;
      }
      removeStatus.textContent = "Removing…";
      removeStatus.style.display = "";
      try {
        await api("POST", "/clients/" + encodeURIComponent(id) + "/unlink");
        state.clientList = (state.clientList || []).filter((c) => c.id !== id);
        rebuildClientOptions();
        clientSelect.value = "";
        removeRow.style.display = "none";
        removeStatus.style.display = "none";
        recheck();
      } catch (e) {
        removeStatus.textContent = "Couldn't remove — " + (e.message || "try again");
      }
    });

    registerBtn.addEventListener("click", async () => {
      newClientStatus.style.display = "none";
      const payload = {
        first_name: newFirst.value.trim(),
        last_name: newLast.value.trim(),
        email: newEmail.value.trim(),
      };
      if (!payload.first_name || !payload.last_name || !payload.email) {
        newClientStatus.textContent = "Please fill in all three fields.";
        newClientStatus.style.display = "";
        return;
      }
      registerBtn.disabled = true;
      registerBtn.textContent = "Registering…";
      try {
        const resp = await api("POST", "/clients/register", payload);
        const newClient = resp.client;
        state.clientList = [newClient].concat(state.clientList || []);
        rebuildClientOptions();
        clientSelect.value = newClient.id;
        addClientForm.style.display = "none";
        newFirst.value = newLast.value = newEmail.value = "";
        recheck();
      } catch (e) {
        newClientStatus.textContent = e.message || "Couldn't register client. Try again.";
        newClientStatus.style.display = "";
      } finally {
        registerBtn.disabled = false;
        registerBtn.textContent = "Register & select";
      }
    });

    cont.addEventListener("click", async () => {
      cont.disabled = true;
      cont.textContent = "Working…";
      state.bagCount = parseInt(bagInput.value, 10);
      state.serviceType = select.value;
      if (state.serviceType !== "personal") {
        state.clientId = clientSelect.value;
        const picked = (state.clientList || []).find((c) => c.id === state.clientId);
        state.clientName = picked ? ((picked.first_name || "") + " " + (picked.last_name || "")).trim() : null;
        state.clientEmail = picked ? (picked.email || null) : null;
      } else {
        state.clientId = null;
        state.clientName = null;
        state.clientEmail = null;
      }
      // Swap to a full-screen loading state — the upstream chain takes a few seconds.
      renderTransitionLoading({
        step: 2, smallCaps: "Ship",
        h1: "Hold tight…",
        lead: "We're preparing your return slot with the carrier.",
      });
      // Step the message so the wait feels intentional rather than stalled.
      const msgs = [
        "Verifying your order…",
        "Booking your return slot…",
        "Loading carrier options…",
      ];
      let i = 0;
      const tick = setInterval(() => { i = Math.min(i + 1, msgs.length - 1); setLoadingMsg(msgs[i]); }, 900);
      try {
        const resp = await api("POST", "/returns/start", {
          bag_count: state.bagCount,
          service_type: state.serviceType,
          customer_id: state.clientId,    // backend expects customer_id (FK to customers table)
          client_name: state.clientName,  // sent for backward-compat / display
        });
        clearInterval(tick);
        state.returnId = resp.return_id;
        state.carriers = resp.carriers || [];
        renderCarrier();
      } catch (e) {
        clearInterval(tick);
        // Re-render the start screen so the user can retry.
        renderStart();
        const card2 = document.querySelector(".rw-rp-card");
        if (card2) showInlineError(card2, e.message);
      }
    });

    body.appendChild(card);
  }

  /* ------------------------------ Screen: carrier ------------------------------ */
  // Real carrier logo SVGs are uploaded to the theme as assets and passed in
  // via data-* attributes on #rw-rp-root. Look them up by canonical name.
  function carrierLogoUrl(name) {
    const lower = (name || "").toLowerCase();
    if (lower.includes("royal mail")) return root.dataset.logoRoyalMail;
    if (lower.includes("dpd"))        return root.dataset.logoDpd;
    if (lower.includes("dhl"))        return root.dataset.logoDhl;
    if (lower.includes("inpost"))     return root.dataset.logoInpost;
    if (lower.includes("yodel"))      return root.dataset.logoYodel;
    return null;
  }

  // Public store-finder URLs — Zigzag's drop-off-points API requires admin
  // tier (same gate as cancellation), so we link out to each carrier's own
  // locator. URLs verified 2026-05-01 (each returns 200 with postcode
  // appended); some accept the postcode query, others ignore it but still
  // render the locator. Royal Mail's Tracked Returns 24 drops at a Post
  // Office, hence postoffice.co.uk for that one.
  function carrierFinderUrl(name, postcode) {
    const lower = (name || "").toLowerCase();
    const pc = encodeURIComponent((postcode || "").trim());
    const q = pc ? "?postcode=" + pc : "";
    if (lower.includes("royal mail")) return "https://www.postoffice.co.uk/branch-finder" + q;
    if (lower.includes("dpd"))        return "https://pickup.dpd.co.uk/" + q;
    if (lower.includes("dhl"))        return "https://locator.dhl.com/results?countryCode=GB" + (pc ? "&postalCode=" + pc : "");
    if (lower.includes("inpost"))     return "https://www.inpost.co.uk/lockers" + q;
    if (lower.includes("yodel"))      return "https://www.yodel.co.uk/store-locator" + q;
    return null;
  }

  function isCollection(carrier) {
    if (typeof carrier.isCollection === "boolean") return carrier.isCollection;
    return /collection/i.test(carrier.serviceType || "");
  }

  // Maps Embed query phrase per carrier. Uses Google Maps's free unauthenticated
  // `output=embed` mode — real markers, no API key needed, no quota concerns.
  // Yodel's drop-off network is branded CollectPlus (their site sets a
  // `_yodel-collectplus_session` cookie); Google Maps indexes those properly,
  // whereas a plain "Yodel" search returns sorting depots not drop-offs.
  function carrierMapQuery(carrierName) {
    const lower = (carrierName || "").toLowerCase();
    if (lower.includes("royal mail")) return "Post Office";
    if (lower.includes("dpd"))        return "DPD parcel shop";
    if (lower.includes("dhl"))        return "DHL ServicePoint";
    if (lower.includes("inpost"))     return "InPost locker";
    if (lower.includes("yodel"))      return "CollectPlus";
    return carrierName + " drop-off";
  }

  // Carriers Google Maps doesn't index well enough for an inline embed —
  // we show a callout + their official finder instead. Yodel's drop-off
  // network is fragmented across partner shops (PayPoint, convenience
  // stores, etc.) and Google returns Amazon Hub Counter locations for
  // "CollectPlus" — useless. Their own locator is the source of truth.
  const NO_MAP_CARRIERS = /yodel/i;

  // Inline drop-off panel: editable postcode + Google Maps embed showing
  // nearest locations for that carrier. Replaces the old "Find nearest ↗"
  // link with an in-page experience. Mounted under each drop-off carrier
  // row, hidden until the row is selected.
  function buildDropoffMapPanel(carrierName, defaultPostcode) {
    const wrap = h("div", { class: "rw-rp-carrier-map" });

    if (NO_MAP_CARRIERS.test(carrierName)) {
      // Map-less variant: just the explainer + CTA. No postcode field — the
      // carrier's own locator collects that, so duplicating it here is noise.
      const note = h("p", {
        class: "rw-rp-map-note",
      }, carrierName + " uses partner shops across the UK — their locator is the most accurate way to find your nearest one.");
      const officialLink = h("a", {
        class: "rw-rp-map-cta",
        href: carrierFinderUrl(carrierName, ""),
        target: "_blank",
        rel: "noopener",
      }, "Open the " + carrierName + " store finder ↗");
      officialLink.addEventListener("click", (e) => e.stopPropagation());
      wrap.appendChild(note);
      wrap.appendChild(officialLink);
      return wrap;
    }

    const label = h("label", { class: "rw-rp-field-label", style: "font-size:11px;letter-spacing:0.04em;" },
      "Show " + carrierName + " near");
    const pcInput = h("input", {
      class: "rw-rp-input",
      type: "text",
      value: (defaultPostcode || "").toUpperCase().trim(),
      placeholder: "Enter a postcode",
      autocomplete: "postal-code",
      style: "max-width:220px;",
    });
    pcInput.addEventListener("click", (e) => e.stopPropagation());
    wrap.appendChild(label);
    wrap.appendChild(pcInput);

    const iframe = h("iframe", {
      class: "rw-rp-map-frame",
      loading: "lazy",
      referrerpolicy: "no-referrer-when-downgrade",
      title: carrierName + " locations",
      allowfullscreen: "true",
    });
    const carrierUrl = carrierFinderUrl(carrierName, pcInput.value);
    const fallback = h("a", {
      class: "rw-rp-carrier-finder",
      style: "margin-top:10px;font-size:11px;",
      href: carrierUrl,
      target: "_blank",
      rel: "noopener",
    }, "Open the official " + carrierName + " finder ↗");
    function updateSrc() {
      const pc = pcInput.value.trim();
      const q = encodeURIComponent(carrierMapQuery(carrierName) + " near " + (pc || "United Kingdom"));
      iframe.src = "https://maps.google.com/maps?q=" + q + "&output=embed&z=14";
      fallback.href = carrierFinderUrl(carrierName, pc);
    }
    let typingTimer = null;
    pcInput.addEventListener("input", () => {
      clearTimeout(typingTimer);
      typingTimer = setTimeout(updateSrc, 400);
    });
    pcInput.addEventListener("keydown", (e) => {
      if (e.key === "Enter") { e.preventDefault(); updateSrc(); }
    });
    iframe.addEventListener("click", (e) => e.stopPropagation());
    fallback.addEventListener("click", (e) => e.stopPropagation());
    updateSrc();
    wrap.appendChild(iframe);
    wrap.appendChild(fallback);
    return wrap;
  }

  // Shared parser for POST /returns/:id/carrier responses.
  // Called by both the drop-off and collection submit handlers so they can
  // never diverge on V2 3-state detection.  Sets:
  //   state.returnIds, state.returnId, state.bookedCount,
  //   state.needsFixingCount, state.failedCount
  function applyBookingResponse(resp) {
    if (resp && resp.return_ids && resp.return_ids.length) {
      // V2 shape carries needs_fixing[]; old shape does not — detect by presence.
      const isV2 = Array.isArray(resp.needs_fixing);
      if (isV2) {
        // Only poll labels for bags in booked[] (address-blocked bags have no label coming).
        // booked[] carries objects {bagIndex, returnId}; extract the string id for the poll URL.
        const bookedIds = Array.isArray(resp.booked) && resp.booked.length
          ? resp.booked.map(function (b) { return (b && typeof b === "object") ? b.returnId : b; })
          : resp.return_ids;
        state.returnIds = bookedIds;
        state.returnId  = bookedIds[0] || resp.return_ids[0];
        state.bookedCount = (typeof resp.booked_count === "number") ? resp.booked_count : bookedIds.length;
        state.needsFixingCount = resp.needs_fixing.length;
        state.failedCount = Array.isArray(resp.failed) ? resp.failed.length : 0;
      } else {
        // Old shape — byte-identical behaviour to before this change.
        state.returnIds = resp.return_ids;
        state.returnId  = resp.return_ids[0];
        state.bookedCount = (typeof resp.booked_count === "number") ? resp.booked_count : resp.return_ids.length;
        state.needsFixingCount = 0;
        state.failedCount = Array.isArray(resp.failed) ? resp.failed.length : 0;
      }
    } else {
      state.failedCount = state.bagCount; // nothing booked
      state.needsFixingCount = 0;
    }
  }

  function renderCarrier() {
    const body = renderShell({
      step: 2,
      smallCaps: "Ship",
      h1: "Pick your return method.",
      lead: "All free. None need a printer.",
    });

    const collection = state.carriers.filter(isCollection);
    const dropoff = state.carriers.filter((c) => !isCollection(c));

    const customerPostcode = (state.order && state.order.shipping_address && state.order.shipping_address.zip) || "";
    const listFor = (carriers) => {
      const list = h("div", { class: "rw-rp-list" });
      carriers.forEach((c) => {
        const url = carrierLogoUrl(c.carrierName);
        const logo = h("div", { class: "rw-rp-logo" },
          url ? h("img", { src: url, alt: c.carrierName }) : null);
        const isDropoff = !isCollection(c);
        const textBlock = h("div", { class: "rw-rp-carrier-text" }, [
          h("div", { class: "rw-rp-carrier-name" }, c.carrierName),
          h("div", { class: "rw-rp-carrier-method" }, c.serviceType || c.serviceName),
        ]);
        const row = h("div", { class: "rw-rp-carrier", "data-id": c.id }, [
          h("div", { class: "rw-rp-radio" }),
          logo,
          textBlock,
          h("div", { class: "rw-rp-chevron" }, "›"),
        ]);
        // Expansion: drop-off carriers get an inline map + editable postcode.
        // Collection carriers don't (Royal Mail comes to your door).
        let expansion = null;
        if (isDropoff) {
          expansion = buildDropoffMapPanel(c.carrierName, customerPostcode);
          expansion.hidden = true;
        }
        row.addEventListener("click", () => {
          body.querySelectorAll(".rw-rp-carrier").forEach((r) => r.classList.remove("selected"));
          body.querySelectorAll(".rw-rp-carrier-map").forEach((m) => m.hidden = true);
          row.classList.add("selected");
          if (expansion) expansion.hidden = false;
          state.selectedCarrier = c;
          cont.disabled = false;
        });
        list.appendChild(row);
        if (expansion) list.appendChild(expansion);
      });
      return list;
    };

    if (collection.length) {
      body.appendChild(h("div", { class: "rw-rp-section-head" }, "Collection — we come to you"));
      body.appendChild(listFor(collection));
    }
    if (dropoff.length) {
      body.appendChild(h("div", { class: "rw-rp-section-head" }, "Drop-off — at a shop or locker"));
      body.appendChild(listFor(dropoff));
    }

    const cont = h("button", { class: "rw-rp-btn" }, "Continue");
    cont.disabled = true;
    cont.addEventListener("click", async () => {
      if (!state.selectedCarrier) return;
      // Guard against a double-tap while the first POST is in flight.
      // renderTransitionLoading replaces the DOM synchronously, but disabling
      // here also covers rapid concurrent touchstart events on mobile.
      if (cont.disabled) return;
      cont.disabled = true;
      if (isCollection(state.selectedCarrier)) {
        renderCollection();
      } else {
        // Drop-off commit. Same loading pattern as collection so the experience
        // is identical regardless of which carrier the user picks.
        const carrierName = state.selectedCarrier.carrierName || "the carrier";
        renderTransitionLoading({
          step: 2, smallCaps: "Almost there",
          h1: "Setting up your return…",
          lead: "Working with " + carrierName + ", just a few seconds.",
        });
        const msgs = [
          "Confirming your bag" + (state.bagCount > 1 ? "s" : "") + "…",
          "Booking with " + carrierName + "…",
          "Generating label" + (state.bagCount > 1 ? "s" : "") + "…",
          "Verifying your label" + (state.bagCount > 1 ? "s" : "") + " with the carrier…",
          "Almost there…",
        ];
        let i = 0;
        // ~1.6s cadence across 5 messages covers the ~7.6s per-bag commit
        // (which verifies each label before returning) without the copy stalling.
        const tick = setInterval(() => { i = Math.min(i + 1, msgs.length - 1); setLoadingMsg(msgs[i]); }, 1600);
        try {
          const resp = await api("POST", "/returns/" + encodeURIComponent(state.returnId) + "/carrier", {
            carrier_service_id: state.selectedCarrier.id,
            bag_count: state.bagCount,
            service_type: state.serviceType,
            customer_id: state.clientId || undefined,
            client_name: state.clientName || undefined,
          });
          clearInterval(tick);
          applyBookingResponse(resp);
          renderLabels();
        } catch (e) {
          clearInterval(tick);
          renderCarrier();
          showInlineError("We could not complete the booking. Please try again, or email hello@rewardedwardrobe.co.uk if it persists.");
        }
      }
    });
    body.appendChild(cont);

    const back = h("button", { class: "rw-rp-back" }, "← Back");
    back.addEventListener("click", renderStartFresh);
    body.appendChild(back);
  }

  /* ------------------------------ Screen: collection ------------------------------ */
  function renderCollection() {
    const isStylist = state.serviceType === "stylist" || state.serviceType === "business";
    const name = isStylist ? state.clientName : "your";
    const body = renderShell({
      step: 2,
      smallCaps: "Ship — collection",
      h1: isStylist ? "Where are " + name + "'s bags?" : "Where are your bags?",
      lead: isStylist
        ? "Give Royal Mail the address to collect from — the client's home, not yours."
        : "Give Royal Mail the address to collect from.",
    });

    // Address block — Google Places Autocomplete + manual fallback fields
    const addrBlock = h("div", { style: "margin-bottom:40px" });
    addrBlock.appendChild(h("span", { class: "rw-rp-block-label" }, "Address"));

    const searchWrap = h("div", { class: "rw-rp-field rw-addr-search", id: "rp-addr-search" });
    searchWrap.appendChild(h("label", { class: "rw-rp-field-label", for: "rp-addr-search-input" }, "Find address"));
    const searchInput = h("input", {
      id: "rp-addr-search-input",
      class: "rw-addr-search__input",
      type: "text",
      autocomplete: "off",
      placeholder: "Start typing the address or postcode",
    });
    searchWrap.appendChild(searchInput);
    const listEl = h("ul", { id: "rp-addr-search-list", class: "rw-addr-search__list", role: "listbox", hidden: true });
    searchWrap.appendChild(listEl);
    addrBlock.appendChild(searchWrap);
    const addrStatus = h("p", { class: "rw-rp-field-hint", id: "rp-addr-status", hidden: true });
    addrBlock.appendChild(addrStatus);

    // Address line 1 used to be a single free-text box. Google's autocomplete
    // returns a street-level match when the exact premises is not indexed, which
    // filled it with a bare street and silently dropped the house number — four
    // of the 49 addresses on file are structurally broken because of it
    // ("Byfleet Road" with nothing else; a postcode sitting in line 1).
    // Capturing the building separately makes that impossible to submit, and
    // gives the carrier its buildingNumber slot directly instead of us
    // regex-guessing it back out of a fused line.
    const buildingInput = h("input", { class: "rw-rp-input", autocomplete: "off", placeholder: "e.g. 12, or Ivy House" });
    // autocomplete off: the browser would put the whole "12 High Street" into
    // the building box (it claims address-line1) and mangle the street. Letting
    // autofill populate only the building composes back to the old value exactly.
    const streetInput = h("input", { class: "rw-rp-input", autocomplete: "off", placeholder: "e.g. High Street" });
    const line2 = h("input", { class: "rw-rp-input", placeholder: "Flat, company, anything else the driver needs" });
    const city = h("input", { class: "rw-rp-input", autocomplete: "address-level2" });
    const postcodeInput = h("input", { class: "rw-rp-input", autocomplete: "postal-code" });
    // Kept so existing code paths that compose a single line still work.
    const line1 = h("input", { type: "hidden" });
    // A failed booking re-renders this whole stage from scratch, which used to
    // hand the customer five empty boxes and make them retype an address they
    // had just typed — and when the failure was the address being rejected,
    // that is an unbreakable loop. booking_failures shows the retry pairs,
    // minutes apart, all failing. Put it back.
    if (state.collectionDraft) {
      const d = state.collectionDraft;
      buildingInput.value = d.building || "";
      streetInput.value = d.street || "";
      line2.value = d.line2 || "";
      city.value = d.city || "";
      postcodeInput.value = d.postcode || "";
    }
    const composeLine1 = () => {
      const b = buildingInput.value.trim();
      const st = streetInput.value.trim();
      // Plenty of people ignore the split and type the whole address into the
      // first box, then add the street underneath — "12 High Street" +
      // "High Street" would compose to "12 High Street High Street" and
      // that is what the driver would read. Don't repeat a street the building
      // field already ends with.
      const bl = b.toLowerCase(), sl = st.toLowerCase();
      const duplicated = !!st && (bl === sl || bl.endsWith(" " + sl) || bl.endsWith("," + sl) || bl.endsWith(", " + sl));
      line1.value = duplicated ? b : [b, st].filter(Boolean).join(" ");
    };

    const addrFields = [
      [ "House number or name", buildingInput, "rp-f-building" ],
      [ "Street or village (leave blank if there isn't one)", streetInput, "rp-f-street" ],
      [ 'Flat, company or extra info <span class="optional">(optional)</span>', line2, "rp-f-line2" ],
      [ "Town or city", city, "rp-f-city" ],
      [ "Postcode", postcodeInput, "rp-f-postcode" ],
    ];
    // Per-field error text, so a blocked Continue always points at the field.
    const fieldErrors = {};
    addrFields.forEach(([label, input, id]) => {
      const f = h("div", { class: "rw-rp-field" });
      f.appendChild(h("label", { class: "rw-rp-field-label", html: label, for: id }));
      input.id = id;
      f.appendChild(input);
      const errEl = h("p", { class: "rw-rp-field-hint", hidden: true });
      errEl.style.color = "var(--rp-error, #9B3C3C)";
      f.appendChild(errEl);
      fieldErrors[id] = errEl;
      addrBlock.appendChild(f);
    });
    addrBlock.appendChild(line1);

    // Validation. Everything is required except the extra-info line. Messages
    // are per-field and only appear once the customer has touched the field or
    // tried to continue, so the form does not shout at them as they type.
    const touched = {};
    function validateAddress(showAll) {
      const problems = {};
      if (!buildingInput.value.trim()) {
        problems["rp-f-building"] = "Enter the house number or name so the driver can find the door.";
      }
      // Street is OPTIONAL. Plenty of real UK addresses have no thoroughfare —
      // a named house in a hamlet, a farm, a croft — and Google returns no
      // `route` for them. Requiring it locked those customers out of booking a
      // collection at all. The BUILDING is still required, so the composed line
      // can never be blank, and that is what both incidents were actually about.
      if (!city.value.trim()) problems["rp-f-city"] = "Enter the town or city.";
      const pc = postcodeInput.value.trim();
      if (!pc) problems["rp-f-postcode"] = "Enter the postcode.";
      else if (!isUkPostcode(pc)) problems["rp-f-postcode"] = "That doesn't look like a UK postcode.";
      Object.keys(fieldErrors).forEach((id) => {
        const msg = problems[id];
        const show = msg && (showAll || touched[id]);
        fieldErrors[id].hidden = !show;
        fieldErrors[id].textContent = show ? msg : "";
      });
      composeLine1();
      return Object.keys(problems).length === 0;
    }
    addrFields.forEach(([, input, id]) => {
      input.addEventListener("blur", () => { touched[id] = true; validateAddress(false); });
      // Typing counts as touching. Marking only on blur meant a customer who
      // filled the address by autocomplete, picked a date, then deleted a
      // character from the postcode saw Continue grey out with no field error,
      // no hint and no status — a silent dead end.
      input.addEventListener("input", () => { touched[id] = true; });
    });

    body.appendChild(addrBlock);

    // Wire up Google Places (New) Autocomplete + Place Details
    // checkReady is defined further down (after the date calendar); the Places
    // callback needs it, so route through a holder assigned once it exists.
    let checkReadyRef = () => {};

    // Inline hint under the address fields, so a disabled Continue button always
    // has a visible reason next to the field that needs fixing.
    const addrHint = h("p", { class: "rw-rp-field-hint", id: "rp-addr-line1-hint", hidden: true });
    addrHint.style.color = "var(--rp-error, #9B3C3C)";
    addrBlock.appendChild(addrHint);
    const setAddrHint = (msg) => {
      addrHint.hidden = !msg;
      addrHint.textContent = msg || "";
    };

    mountPlacesAutocomplete({
      key: window.RW_GOOGLE_PLACES_KEY,
      searchInput, listEl, addrStatus,
      buildingInput, streetInput, line2, city, postcodeInput,
      onApplied: () => { composeLine1(); checkReadyRef(); },
    });

    // Date list
    const dateBlock = h("div", { style: "margin-bottom:40px" });
    dateBlock.appendChild(h("span", { class: "rw-rp-block-label" }, "Day"));
    dateBlock.appendChild(h("div", { class: "rw-rp-section-title" }, "Pick a collection day"));
    dateBlock.appendChild(h("p", { class: "rw-rp-section-sub" }, "Royal Mail collects Monday to Saturday. Pick any day that suits you."));

    const dates = state.selectedCarrier.availableCollectionDates || [];
    if (!dates.length) {
      // Nothing bookable (bank-holiday cluster, or the carrier returned no days).
      // Without this the calendar renders every cell disabled with no message and
      // Continue never enables, which is a silent dead end.
      dateBlock.appendChild(h("p", { class: "rw-rp-section-sub" },
        "No collection days are available at the moment. Go back and choose a drop-off option instead \u2014 the label doesn't expire, so you can take the bags whenever suits."));
      body.appendChild(dateBlock);
      const back = h("button", { class: "rw-rp-btn" }, "Choose another option");
      back.addEventListener("click", function () { renderCarrier(); });
      body.appendChild(back);
      return;
    }
    const availableSet = new Set(dates);
    let selectedDate = null;

    // Calendar — show the month spanning the available dates. If dates roll
    // into a second month, show whichever has more selectable days first.
    const calWrap = h("div", { class: "rw-rp-cal" });
    const earliest = dates.length ? new Date(dates[0]) : new Date();
    const latest = dates.length ? new Date(dates[dates.length - 1]) : new Date();

    function renderMonth(year, month) {
      calWrap.innerHTML = "";
      const monthStart = new Date(Date.UTC(year, month, 1));
      const daysInMonth = new Date(Date.UTC(year, month + 1, 0)).getUTCDate();
      const monthLabel = monthStart.toLocaleDateString("en-GB", { month: "long", year: "numeric" });

      // Header — month label + prev/next arrows (disable when out of range)
      const header = h("div", { class: "rw-rp-cal-head" });
      const prev = h("button", { class: "rw-rp-cal-arrow", type: "button", "aria-label": "Previous month" }, "‹");
      const next = h("button", { class: "rw-rp-cal-arrow", type: "button", "aria-label": "Next month" }, "›");
      const earliestKey = earliest.getUTCFullYear() * 12 + earliest.getUTCMonth();
      const latestKey = latest.getUTCFullYear() * 12 + latest.getUTCMonth();
      const currentKey = year * 12 + month;
      prev.disabled = currentKey <= earliestKey;
      next.disabled = currentKey >= latestKey;
      prev.addEventListener("click", () => renderMonth(year, month - 1));
      next.addEventListener("click", () => renderMonth(year, month + 1));
      header.appendChild(prev);
      header.appendChild(h("span", { class: "rw-rp-cal-title" }, monthLabel));
      header.appendChild(next);
      calWrap.appendChild(header);

      // Day-of-week heading (Mon-first to match UK convention)
      const dowHead = h("div", { class: "rw-rp-cal-grid rw-rp-cal-dow" });
      ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"].forEach((d) =>
        dowHead.appendChild(h("span", { class: "rw-rp-cal-dow-cell" }, d)));
      calWrap.appendChild(dowHead);

      // Cells — leading blanks so day-1 lands under correct weekday.
      const grid = h("div", { class: "rw-rp-cal-grid" });
      const firstDow = monthStart.getUTCDay(); // 0=Sun..6=Sat
      const leading = (firstDow + 6) % 7; // shift so Mon=0
      for (let i = 0; i < leading; i++) grid.appendChild(h("span", { class: "rw-rp-cal-cell rw-rp-cal-empty" }));

      for (let day = 1; day <= daysInMonth; day++) {
        const iso = new Date(Date.UTC(year, month, day)).toISOString().slice(0, 10);
        const isAvailable = availableSet.has(iso);
        const cell = h("button", {
          type: "button",
          class: "rw-rp-cal-cell" + (isAvailable ? " is-available" : " is-disabled") + (selectedDate === iso ? " is-selected" : ""),
          "data-iso": iso,
          disabled: isAvailable ? null : "disabled",
        }, String(day));
        if (isAvailable) {
          cell.addEventListener("click", () => {
            calWrap.querySelectorAll(".rw-rp-cal-cell").forEach((c) => c.classList.remove("is-selected"));
            cell.classList.add("is-selected");
            selectedDate = iso;
            checkReady();
          });
        }
        grid.appendChild(cell);
      }
      calWrap.appendChild(grid);
    }
    renderMonth(earliest.getUTCFullYear(), earliest.getUTCMonth());

    dateBlock.appendChild(calWrap);
    dateBlock.appendChild(h("p", { class: "rw-rp-date-note" }, "Greyed days aren't bookable. Need longer than a week? Use a drop-off option — the label doesn't expire."));
    body.appendChild(dateBlock);

    const cont = h("button", { class: "rw-rp-btn" }, "Continue");
    cont.disabled = true;
    const checkReady = () => {
      // House number/name, street, town and postcode are all required; only the
      // extra-info line is optional. The building field being separate is what
      // makes a doorless address impossible to submit.
      const addressOk = validateAddress(false);
      cont.disabled = !(addressOk && selectedDate);
      setAddrHint(!addressOk && (selectedDate || Object.values(touched).some(Boolean))
        ? "Please finish the address above before continuing."
        : "");
    };
    [buildingInput, streetInput, line2, city, postcodeInput].forEach((el) => el.addEventListener("input", checkReady));
    checkReadyRef = checkReady;

    cont.addEventListener("click", async () => {
      // Guard against a double-tap — the button starts enabled once the form
      // is complete; disable immediately so only the first tap fires a POST.
      if (cont.disabled) return;
      cont.disabled = true;
      // Capture the address values before the screen swaps to loading.
      // Re-validate at the moment of submit rather than trusting the button
      // state, and surface every outstanding problem at once.
      if (!validateAddress(true)) { cont.disabled = true; return; }
      composeLine1();
      const addrSnap = {
        // Sent separately so the carrier gets its buildingNumber slot directly
        // instead of the server regex-guessing it back out of a fused line.
        building: buildingInput.value.trim(),
        street: streetInput.value.trim(),
        line1: line1.value.trim(),
        line2: line2.value.trim(),
        city: city.value.trim(),
        postcode: postcodeInput.value.trim(),
      };
      const dateSnap = selectedDate;
      const carrierName = (state.selectedCarrier && state.selectedCarrier.carrierName) || "the carrier";
      renderTransitionLoading({
        step: 2, smallCaps: "Almost there",
        h1: "Setting up your return…",
        lead: "Working with " + carrierName + ", just a few seconds.",
      });
      const msgs = [
        "Confirming your bag" + (state.bagCount > 1 ? "s" : "") + "…",
        "Booking with " + carrierName + "…",
        "Generating label" + (state.bagCount > 1 ? "s" : "") + "…",
        "Verifying your label" + (state.bagCount > 1 ? "s" : "") + " with the carrier…",
        "Almost there…",
      ];
      let i = 0;
      // ~1.6s cadence across 5 messages covers the ~7.6s per-bag commit.
      const tick = setInterval(() => { i = Math.min(i + 1, msgs.length - 1); setLoadingMsg(msgs[i]); }, 1600);
      try {
        const resp = await api("POST", "/returns/" + encodeURIComponent(state.returnId) + "/carrier", {
          carrier_service_id: state.selectedCarrier.id,
          collection_date: dateSnap,
          collection_address: {
            buildingIdentifier: addrSnap.building || undefined,
            street: addrSnap.street || undefined,
            addressLine1: addrSnap.line1,
            addressLine2: addrSnap.line2 || undefined,
            city: addrSnap.city,
            postcode: addrSnap.postcode,
          },
          bag_count: state.bagCount,
          service_type: state.serviceType,
          customer_id: state.clientId || undefined,
          client_name: state.clientName || undefined,
        });
        clearInterval(tick);
        applyBookingResponse(resp);
        state.collectionDraft = null;
        state.collectionDraftDate = null;
        state.collection = { date: dateSnap, address: addrSnap };
        renderLabels();
      } catch (e) {
        clearInterval(tick);
        // Keep what they typed so the re-render can restore it.
        state.collectionDraft = addrSnap;
        state.collectionDraftDate = dateSnap;
        renderCollection();
        // The server sends a specific reason — a rejected address, a missing
        // town — precisely so the customer can fix it. Swallowing it behind a
        // fixed string left them guessing which field was wrong, on a form that
        // had just been blanked.
        const reason = (e && e.message) ? String(e.message) : "";
        showInlineError(
          reason
            ? reason + " Your address is still here — correct it and try again."
            : "We could not complete the booking. Your address is still here — please try again, or email hello@rewardedwardrobe.co.uk if it persists.",
        );
      }
    });
    body.appendChild(cont);

    const back = h("button", { class: "rw-rp-back" }, "← Back");
    back.addEventListener("click", renderCarrier);
    body.appendChild(back);
  }

  /* ------------------------------ Screen: labels ------------------------------ */
  async function renderLabels() {
    // Skeleton loading — show immediately so the user isn't staring at the
    // previous screen while we poll Zigzag.
    const carrierName = (state.selectedCarrier && state.selectedCarrier.carrierName) || "The carrier";
    const body = renderShell({
      step: 3,
      smallCaps: "Almost there",
      h1: "Generating your labels…",
      lead: carrierName + " is preparing your return — usually 5–15 seconds.",
    });
    const wrap = h("div", { class: "rw-rp-loading" }, [
      h("div", { class: "rw-rp-spinner" }),
      h("p", { class: "rw-rp-loading-msg", id: "rw-rp-labels-msg" }, "Talking to the carrier…"),
    ]);
    body.appendChild(wrap);

    const setMsg = (m) => {
      const el = document.getElementById("rw-rp-labels-msg");
      if (el) el.textContent = m;
    };

    // Poll /labels for EACH return (one per bag) until all are ready (or time out).
    // Up to 8 rounds of 2s polling = 16s ceiling. Partial success is allowed:
    // if at least one label is ready we render those and let the banner explain the rest.
    const ids = state.returnIds && state.returnIds.length ? state.returnIds : [state.returnId];
    const results = ids.map(() => null);
    for (let round = 0; round < 8; round++) {
      const promises = ids.map(async (rid, idx) => {
        if (results[idx] && results[idx].status === "ready") return;
        try {
          const r = await api("GET", "/returns/" + encodeURIComponent(rid) + "/labels");
          results[idx] = r;
        } catch (_e) { /* retry next round */ }
      });
      await Promise.all(promises);
      const allReady = results.every((r) => r && r.status === "ready");
      if (allReady) break;
      if (round === 1) setMsg(carrierName + " is on it — almost there…");
      if (round === 4) setMsg("Just a few more seconds…");
      await new Promise((r) => setTimeout(r, 2000));
    }

    // Render whatever is ready; only show the error screen when zero labels are ready.
    const readyLabels = results
      .map((r, i) => (r && r.status === "ready") ? Object.assign({}, r, { return_id: ids[i] }) : null)
      .filter(Boolean);
    if (readyLabels.length > 0) {
      // Do NOT clobber state.bookedCount here — it holds the server truth
      // (booked_count from /carrier). readyLabels.length only reflects labels
      // that finished generating within the poll window; a booked-but-slow
      // label would otherwise be mislabelled as "failed".
      state.labelData = readyLabels;
      return renderLabelsReady(readyLabels);
    }
    const rejected = results.find((r) => r && r.status === "carrier_rejected");
    if (rejected) return renderLabelsError(rejected);
    return renderLabelsError(results.find((r) => r && r.status !== "ready") || null);
  }

  function renderLabelsReady(resp) {
    const body = document.getElementById("rw-rp-body");
    body.innerHTML = "";
    const isCol = !!state.collection;
    const isStylist = state.serviceType === "stylist" || state.serviceType === "business";
    // Hoist labels array and compute counts from SERVER truth (not the rendered
    // ready-label count). state.bookedCount = booked_count from /carrier (bags
    // actually booked); state.failedCount = failed.length from /carrier (bags
    // that truly failed to book). Backward-compat: the old {ok, return_ids}
    // response shape sets bookedCount to return_ids.length and failedCount to 0.
    const labels = Array.isArray(resp) ? resp : [resp];
    const readyN = labels.length;                                  // labels rendered ready in the poll window
    const bookedN = (typeof state.bookedCount === "number") ? state.bookedCount : readyN; // bags actually booked (label ready or coming)
    const needsFixingN = state.needsFixingCount || 0;              // bags booked but label blocked — address needs fixing
    const failedN = state.failedCount || 0;                        // bags that truly failed to book
    const pendingN = Math.max(0, bookedN - readyN);                // booked, label still generating

    // Don't claim "All labels emailed" when some bags failed, are still
    // generating, or need an address fix — any of these would contradict the
    // banners below.
    const emailedCopy = (failedN > 0 || pendingN > 0 || needsFixingN > 0)
      ? "Your booked labels have been emailed to <strong>" + escapeHtml(state.order.email) + "</strong>. Tracking updates will follow as the bags move."
      : "All labels emailed to <strong>" + escapeHtml(state.order.email) + "</strong>. Tracking updates will follow as the bags move.";
    body.appendChild(
      h("div", { class: "rw-rp-confirm-strip" }, [
        h("div", { html: emailedCopy }),
      ]),
    );

    // Partial-success banners — three distinct cases rendered independently so
    // a mixed response shows all relevant sections with honest copy.
    //
    //  - needsFixingN > 0: bags that ARE booked but whose label is blocked by a
    //    carrier address rejection (code 163). They are NOT failed — the booking
    //    exists — but the label cannot be made until the address is confirmed.
    //    Do NOT say "emailing shortly": no label is coming until the fix is made.
    //
    //  - failedN > 0: bags that genuinely could not be booked. The team is
    //    notified; a straggler that booked but was not recorded will be reconciled
    //    manually, so copy must be reassuring rather than alarming.
    //
    //  - pendingN > 0: bags that DID book and whose label is still generating —
    //    those genuinely get emailed asynchronously; "emailing shortly" is correct.
    if (needsFixingN > 0) {
      body.appendChild(
        h("div", { class: "rw-rp-note rw-rp-note-warn" },
          needsFixingN + " bag" + (needsFixingN === 1 ? " is" : "s are") + " booked, but we need to confirm a delivery address before the label can be made. We will be in touch."
        )
      );
    }
    if (failedN > 0) {
      body.appendChild(
        h("div", { class: "rw-rp-note rw-rp-note-warn" },
          failedN + " bag" + (failedN === 1 ? "" : "s") + " could not be booked automatically and our team has been notified. We will follow up with you by email."
        )
      );
    }
    if (pendingN > 0) {
      body.appendChild(
        h("div", { class: "rw-rp-note rw-rp-note-warn" },
          "Your remaining " + pendingN + " label" + (pendingN === 1 ? "" : "s") + " " + (pendingN === 1 ? "is" : "are") + " being prepared and will be emailed shortly."
        )
      );
    }

    // What happens next
    const wn = h("div", { class: "rw-rp-what-next" });
    wn.appendChild(h("span", { class: "rw-rp-what-next-label" }, "What happens next"));
    if (isCol) {
      const addr = state.collection.address;
      wn.appendChild(h("h3", { class: "rw-rp-what-next-title" }, formatDate(state.collection.date) + ", 8am–6pm"));
      wn.appendChild(h("div", {
        class: "rw-rp-what-next-body",
        html:
          // Show BOTH address lines. For a named building the street now lives
          // on line2 (see mapPlaceLines), so printing line1 alone would confirm
          // the collection to a house name with no street.
          "Royal Mail will arrive at <strong>" +
          escapeHtml([addr.line1, addr.line2, addr.city, addr.postcode].filter(Boolean).join(", ")) +
          "</strong> with the labels already printed. " +
          (isStylist ? (escapeHtml(state.clientName) + " just needs to hand over") : "Just hand over") +
          " the " + bookedN + " bag" + (bookedN > 1 ? "s" : "") +
          ", or leave them somewhere they can be safely collected.",
      }));
    } else {
      const carrier = state.selectedCarrier.carrierName;
      const customerPostcode = (state.order && state.order.shipping_address && state.order.shipping_address.zip) || "";
      const finderUrl = carrierFinderUrl(carrier, customerPostcode);
      wn.appendChild(h("h3", { class: "rw-rp-what-next-title" }, "Drop off at any " + carrier + " shop"));
      wn.appendChild(h("div", {
        class: "rw-rp-what-next-body",
        html:
          "Show the QR code from your email on your phone — staff scan it, print the label, stick it on the bag, and hand you a receipt. No booking, any time the shop's open.",
      }));
      if (finderUrl) {
        wn.appendChild(h("a", {
          class: "rw-rp-carrier-finder",
          style: "margin-top:14px;font-size:12px;",
          href: finderUrl,
          target: "_blank",
          rel: "noopener",
        }, "Find your nearest " + carrier + " shop ↗"));
      }
    }
    body.appendChild(wn);

    // Auto-fire send-to-stylist exactly once per returnId so the stylist's
    // own inbox always gets the labels on file. We key the dedupe on the
    // primary return UUID so navigating back-and-forward in the SPA doesn't
    // re-send. Stylist mode only.
    if (isStylist && state.returnId) {
      if (!state.sentToStylist) state.sentToStylist = {};
      if (!state.sentToStylist[state.returnId]) {
        state.sentToStylist[state.returnId] = "sending";
        (async () => {
          try {
            await api("POST", "/returns/" + encodeURIComponent(state.returnId) + "/send-to-stylist", {});
            state.sentToStylist[state.returnId] = "sent";
          } catch (e) {
            state.sentToStylist[state.returnId] = "failed";
            console.error("send-to-stylist failed:", e && e.message);
          }
        })();
      }
    }

    // Personal returns: auto-fire send-to-client exactly once per returnId so
    // the customer who just booked actually receives the branded labels email
    // (the confirmation strip above promises it). Mirrors the stylist auto-send.
    // Stylist/business clients are NOT auto-sent here — the stylist controls
    // that via the explicit "Send to client" card below.
    if (!isStylist && state.returnId) {
      if (!state.sentToClient) state.sentToClient = {};
      if (!state.sentToClient[state.returnId]) {
        state.sentToClient[state.returnId] = "sending";
        (async () => {
          try {
            await api("POST", "/returns/" + encodeURIComponent(state.returnId) + "/send-to-client", {
              client_email: state.order.email,
            });
            state.sentToClient[state.returnId] = "sent";
          } catch (e) {
            state.sentToClient[state.returnId] = "failed";
            console.error("send-to-client failed:", e && e.message);
          }
        })();
      }
    }

    // Stylist-for-client returns: auto-fire send-to-client exactly once per
    // returnId, same as the personal-return auto-send above, so the client
    // always gets their labels even if the stylist never clicks the manual
    // "Send to client" button below (or their inbox is dead). The stylist
    // still gets their own copy via the send-to-stylist auto-fire above.
    // The endpoint defaults the recipient to the client's registered email
    // server-side, so no client_email is passed here. Same dedupe object
    // (state.sentToClient, keyed by returnId) as the personal path — the two
    // branches are mutually exclusive per render, so there's no collision.
    if (isStylist && state.returnId) {
      if (!state.sentToClient) state.sentToClient = {};
      if (!state.sentToClient[state.returnId]) {
        state.sentToClient[state.returnId] = "sending";
        (async () => {
          try {
            await api("POST", "/returns/" + encodeURIComponent(state.returnId) + "/send-to-client", {});
            state.sentToClient[state.returnId] = "sent";
          } catch (e) {
            state.sentToClient[state.returnId] = "failed";
            console.error("send-to-client (stylist-for-client auto) failed:", e && e.message);
          }
        })();
      }
    }

    // Stylist send-to-client card. Uses the registered client email captured
    // when the client was picked at the start of the flow. If the client has
    // no email on record, fall back to prompting the stylist for one.
    // Kept as a manual resend fallback even though labels now auto-send to
    // the client above (useful if the client's email bounced or changed).
    if (isStylist) {
      const registeredEmail = state.clientEmail || "";
      const subline = registeredEmail
        ? "We'll send the labels and details to " + registeredEmail + "."
        : "No registered email — we'll ask for one when you click send.";
      const card = h("div", { class: "rw-rp-stylist-send" }, [
        h("div", null, [
          h("div", { class: "rw-rp-stylist-send-title" }, "Send the details to " + state.clientName + "?"),
          h("div", { class: "rw-rp-stylist-send-sub" }, subline),
        ]),
        (() => {
          const btn = h("button", { class: "rw-rp-stylist-send-btn" }, "Send to client");
          btn.addEventListener("click", async () => {
            let email = registeredEmail;
            if (!email) {
              email = prompt("Email address for " + state.clientName + "?");
              if (!email) return;
            }
            btn.textContent = "Sending…";
            btn.disabled = true;
            try {
              await api("POST", "/returns/" + encodeURIComponent(state.returnId) + "/send-to-client", {
                client_email: email,
              });
              btn.textContent = "Sent ✓";
            } catch (e) {
              btn.textContent = "Send to client";
              btn.disabled = false;
              alert("Could not send: " + e.message);
            }
          });
          return btn;
        })(),
      ]);
      body.appendChild(card);
    }

    // Labels list
    const head = h("div", { class: "rw-rp-labels-head" }, [
      h("h3", { class: "rw-rp-labels-title" }, "Your " + bookedN + " return" + (bookedN > 1 ? "s" : "")),
      h("span", { class: "rw-rp-labels-meta" }, state.selectedCarrier.carrierName + (state.selectedCarrier.serviceName ? " · " + state.selectedCarrier.serviceName : "")),
    ]);
    body.appendChild(head);

    // `labels` is already an array (hoisted above) — one label per bag, each with its own pdf_base64.
    // Pre-build blobs so each Download button can fire instantly.
    const blobs = labels.map((l) => base64ToBlob(l.pdf_base64, "application/pdf"));

    const list = h("div", { class: "rw-rp-list" });
    for (let i = 0; i < labels.length; i++) {
      const t = (labels[i].tracking || [])[0] || {};
      const trackUrl = t.carrier_tracking_url;
      const trackId = t.carrier_tracking_id || "—";
      const steps = isCol ? ["Booked", "Collected", "With us"] : ["Label ready", "Dropped off", "With us"];
      const row = h("div", { class: "rw-rp-label-row" }, [
        h("div", null, [
          h("div", { class: "rw-rp-label-name" }, "Bag " + (i + 1) + " of " + labels.length),
          h("div", {
            class: "rw-rp-label-ret-id",
            html: trackUrl
              ? 'Tracking <a href="' + escapeHtml(trackUrl) + '" target="_blank" rel="noopener">' + escapeHtml(trackId) + " ↗</a>"
              : "Tracking " + escapeHtml(trackId),
          }),
          h("div", { class: "rw-rp-status-line" }, [
            h("span", { class: "rw-rp-status-dot current" }),
            h("span", { class: "rw-rp-status-step current" }, steps[0]),
            h("span", null, " "),
            h("span", { class: "rw-rp-status-dot" }),
            h("span", { class: "rw-rp-status-step" }, steps[1]),
            h("span", null, " "),
            h("span", { class: "rw-rp-status-dot" }),
            h("span", { class: "rw-rp-status-step" }, steps[2]),
          ]),
        ]),
        (() => {
          const idx = i;
          const btn = h("button", { class: "rw-rp-dl-btn" }, "Download PDF");
          btn.addEventListener("click", () => downloadBlob(blobs[idx], "rw-return-" + (labels[idx].return_id || "bag") + ".pdf"));
          return btn;
        })(),
      ]);
      list.appendChild(row);
    }
    body.appendChild(list);

    // Bulk actions
    const bulk = h("div", { class: "rw-rp-bulk-actions" });
    const dlAll = h("button", { class: "rw-rp-return-more-btn" }, "Download all labels");
    dlAll.addEventListener("click", async () => {
      if (labels.length === 1) {
        downloadBlob(blobs[0], "rw-return-" + (labels[0].return_id || "all") + ".pdf");
        return;
      }
      dlAll.disabled = true;
      const original = dlAll.textContent;
      dlAll.textContent = "Merging…";
      try {
        const merged = await mergePdfs(labels.map((l) => l.pdf_base64));
        const fname = "rw-returns-" + state.order.order_number.replace(/^#/, "") + "-all-" + labels.length + "-bags.pdf";
        downloadBlob(merged, fname);
      } catch (e) {
        // Fallback: trigger separate downloads with a small stagger.
        labels.forEach((l, idx) => setTimeout(() => downloadBlob(blobs[idx], "rw-return-" + (l.return_id || "bag-" + (idx+1)) + ".pdf"), idx * 250));
      } finally {
        dlAll.textContent = original;
        dlAll.disabled = false;
      }
    });
    bulk.appendChild(dlAll);
    const resend = h("button", { class: "rw-rp-btn-ghost rw-rp-dl-btn", style: "padding:14px 24px; font-size:14px" }, "Resend email");
    resend.addEventListener("click", async () => {
      resend.textContent = "Sending…";
      resend.disabled = true;
      try {
        await api("POST", "/returns/" + encodeURIComponent(state.returnId) + "/resend-email");
        resend.textContent = "Sent ✓";
      } catch (e) {
        resend.disabled = false;
        resend.textContent = "Resend email";
        alert("Could not resend: " + e.message);
      }
    });
    bulk.appendChild(resend);
    body.appendChild(bulk);

    // Cancel-collection UI removed 2026-05-06: stylists don't cancel
    // returns themselves — RW handles cancellations from Zigzag's
    // dashboard, which the poll-returns-tracking job picks up and
    // restores inventory accordingly. If a stylist needs to cancel,
    // they message RW. Original handler available in git history.

    // Return more CTA. Uses the live bags_remaining count (which already
    // excludes cancelled returns) as the basis, minus what we just booked.
    const baseRemaining = (typeof state.order.bags_remaining === "number")
      ? state.order.bags_remaining
      : state.order.bags_ordered;
    const remainingAfter = baseRemaining - bookedN;
    if (remainingAfter > 0) {
      const rm = h("div", { class: "rw-rp-return-more" }, [
        h("div", null, [
          h("h3", { class: "rw-rp-return-more-title" }, "More bags to return?"),
          h("div", { class: "rw-rp-return-more-sub" },
            "You've got " + remainingAfter + " bag" + (remainingAfter > 1 ? "s" : "") +
            " left on this order. Start another session — same order number, fresh labels."),
        ]),
        (() => {
          const btn = h("button", { class: "rw-rp-return-more-btn" }, "Return more bags");
          btn.addEventListener("click", () => {
            state.returnId = null;
            state.carriers = [];
            state.selectedCarrier = null;
            state.collection = null;
            state.labelData = null;
            renderStartFresh();
          });
          return btn;
        })(),
      ]);
      body.appendChild(rm);
    }
  }

  function renderLabelsError(resp) {
    const rejected = resp && resp.status === "carrier_rejected";
    const body = renderShell({
      step: 3,
      smallCaps: rejected ? "Carrier hiccup" : "Almost there",
      h1: rejected ? "We hit a snag with the carrier." : "Hang on a moment.",
      lead: rejected
        ? "Your booking went through, but the carrier couldn't accept it just yet."
        : "Your return is booked — we're just finalising your labels with the carrier.",
    });
    const message = rejected
      ? "<strong>" + (resp.carrier || "The carrier") + " response:</strong> " + (resp.error || "Unknown error") + "<br>" +
        "We've logged this — please email support so we can get your labels reissued by hand."
      : "<strong>Labels are taking a little longer than usual.</strong> " +
        ((state.selectedCarrier && state.selectedCarrier.carrierName) || "The carrier") +
        " hasn't returned them yet — this is usually a short delay. We'll email them to you within the next 15 minutes. You can close this page.";
    body.appendChild(
      h("div", { class: "rw-rp-error-strip" }, [
        h("div", { html: message }),
      ]),
    );
    const actions = h("div", { class: "rw-rp-bulk-actions" });
    const retry = h("button", { class: "rw-rp-btn-ghost rw-rp-dl-btn", style: "padding:14px 24px; font-size:14px" }, "Try again now");
    retry.addEventListener("click", () => renderLabels());
    actions.appendChild(retry);
    const support = h("a", { class: "rw-rp-btn-ghost rw-rp-dl-btn", href: "mailto:" + SUPPORT_EMAIL, style: "padding:14px 24px; font-size:14px; text-decoration:none; text-align:center" }, "Email support");
    actions.appendChild(support);
    body.appendChild(actions);
  }

  /* ------------------------------ Google Places Autocomplete ------------------------------ */
  // Mirrors the pattern in rw-pre-checkout.liquid + rw-client-completion.liquid:
  //   - POST https://places.googleapis.com/v1/places:autocomplete (input + sessionToken, GB only)
  //   - GET  https://places.googleapis.com/v1/places/{placeId}?sessionToken=... (addressComponents)
  //   - parses components into address line 1 / 2 / city / postcode
  // UK postcode shape. Deliberately permissive on spacing and case; this is a
  // typo catch, not PAF validation.
  function isUkPostcode(v) {
    const t = (v || "").trim();
    // GIR 0AA is genuine (Girobank, Bootle) and matches no standard pattern.
    if (/^GIR\s*0AA$/i.test(t)) return true;
    return /^[A-Z]{1,2}[0-9][A-Z0-9]?\s*[0-9][A-Z]{2}$/i.test(t);
  }

  // Pull a leading building identifier ("29", "29a", "Flat 6") out of whatever
  // the customer typed into the search box. Used to restore a house number that
  // Google dropped — see applyAddressComponents.
  function leadingBuildingId(typed) {
    const t = (typed || "").trim();
    const m = t.match(/^(?:flat|apt|apartment|unit|suite|no\.?)\s*(?:[0-9]+[a-zA-Z]?|[a-zA-Z])\b/i) ||
      t.match(/^[0-9]+[a-zA-Z]?(?:-[0-9]+[a-zA-Z]?)?\b/);
    return m ? m[0].trim() : "";
  }

  // NOTE: mapPlaceLines() and line1HasBuildingId() were removed 2026-09-21.
  // The address form now captures the building and the street as separate
  // required fields, so there is no fused line to map or to inspect for a
  // building identifier. applyAddressComponents derives the fields directly.

  function mountPlacesAutocomplete({ key, searchInput, listEl, addrStatus, buildingInput, streetInput, line2, city, postcodeInput, onApplied }) {
    // Provenance, not guesswork. A picked address OWNS the boxes it filled, so a
    // later pick may overwrite or clear them; anything the CUSTOMER typed is
    // theirs and survives. Without this the two failure modes are
    // irreconcilable: an already-present "29" must be cleared when the customer
    // picks a different street (else we compose "29 Other Road", a real house
    // on the wrong road), while a hand-typed "12" must NOT be (the 2026-09-21
    // bug, where the number the customer typed was silently wiped). Both are
    // "a value the pick did not write"; only provenance separates them.
    const lastAuto = { building: "", street: "", extra: "", city: "", postcode: "" };
    const setDerived = (el, key, value) => {
      const cur = (el.value || "").trim();
      if (cur !== "" && cur !== lastAuto[key]) return; // the customer's, leave it
      el.value = value || "";
      lastAuto[key] = value || "";
    };
    function setStatus(msg, tone) {
      if (!msg) { addrStatus.hidden = true; addrStatus.textContent = ""; return; }
      addrStatus.hidden = false;
      addrStatus.textContent = msg;
      if (tone === "error") addrStatus.style.color = "var(--rp-error, #9B3C3C)";
      else addrStatus.style.color = "";
    }
    if (!key) {
      setStatus("Address autocomplete unavailable. Enter your address manually below.", "error");
      searchInput.disabled = true;
      return;
    }
    function uuid() {
      return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
        const r = (Math.random() * 16) | 0;
        return (c === "x" ? r : (r & 0x3) | 0x8).toString(16);
      });
    }
    let sessionToken = uuid();
    let debounceTimer = null;
    let lastQuery = "";

    function clearList() {
      listEl.innerHTML = "";
      listEl.hidden = true;
    }

    function renderSuggestions(suggestions) {
      listEl.innerHTML = "";
      if (!suggestions || !suggestions.length) { listEl.hidden = true; return; }
      suggestions.forEach((s) => {
        const pred = s.placePrediction;
        if (!pred) return;
        const li = document.createElement("li");
        li.className = "rw-addr-search__item";
        li.setAttribute("role", "option");
        li.dataset.placeId = pred.placeId;
        li.textContent = (pred.text && pred.text.text) || "";
        li.addEventListener("mousedown", (evt) => {
          evt.preventDefault();
          selectPlace(pred.placeId, li.textContent);
        });
        listEl.appendChild(li);
      });
      listEl.hidden = false;
    }

    function fetchSuggestions(q) {
      fetch("https://places.googleapis.com/v1/places:autocomplete", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-Goog-Api-Key": key,
          "X-Goog-FieldMask": "suggestions.placePrediction.placeId,suggestions.placePrediction.text",
        },
        body: JSON.stringify({
          input: q,
          sessionToken: sessionToken,
          includedRegionCodes: ["gb"],
          languageCode: "en-GB",
        }),
      })
        .then((r) => { if (!r.ok) throw new Error("autocomplete " + r.status); return r.json(); })
        .then((d) => renderSuggestions(d.suggestions || []))
        .catch((e) => {
          console.error("places autocomplete", e);
          clearList();
          setStatus("Address lookup is temporarily unavailable. Enter your address manually below.", "error");
        });
    }

    function selectPlace(placeId, label) {
      searchInput.value = label;
      clearList();
      fetch("https://places.googleapis.com/v1/places/" + encodeURIComponent(placeId) + "?sessionToken=" + sessionToken, {
        headers: {
          "X-Goog-Api-Key": key,
          "X-Goog-FieldMask": "addressComponents,formattedAddress",
        },
      })
        .then((r) => { if (!r.ok) throw new Error("details " + r.status); return r.json(); })
        .then((d) => {
          // applyAddressComponents returns true when it had to warn (no street
          // in the chosen place) — don't clear that warning straight away.
          const messaged = applyAddressComponents(d);
          sessionToken = uuid();
          if (!messaged) setStatus("");
        })
        .catch((e) => {
          console.error("places details", e);
          setStatus("Could not load that address. Enter it manually below.", "error");
        });
    }

    function applyAddressComponents(place) {
      const parts = {};
      (place.addressComponents || []).forEach((c) => {
        (c.types || []).forEach((t) => { parts[t] = c; });
      });
      const val = (t) => (parts[t] ? (parts[t].longText || parts[t].shortText || "") : "");

      const streetNumber = val("street_number");
      const route = val("route");
      const premiseRaw = (val("premise") || "").split(",")[0].trim();
      const subpremise = val("subpremise");
      const premiseIsNumber = /^[0-9]+[a-zA-Z]?$/.test(premiseRaw);

      // The building identifier, in the order Royal Mail cares about: an actual
      // street number, else a numeric premise standing in for one, else the
      // house name. This is the field that used to vanish.
      let building = streetNumber || (premiseIsNumber ? premiseRaw : "") || (premiseRaw || "");
      // Street-level match: Google gives a route but no number at all. Recover
      // the number from what the customer TYPED — selectPlace has already
      // overwritten searchInput.value with the suggestion label by this point,
      // so reading that box recovers nothing (audited 2026-09-21: the fallback
      // fired 41 times and contributed a building identifier zero times).
      if (!building) building = leadingBuildingId(lastQuery);
      // Rural named houses ("Ty Newydd, Criccieth") come back with no premise
      // and no number to recover, but the chosen suggestion names the house in
      // its first segment. Use that, unless it is just the street repeated.
      if (!building) {
        // Only usable when the first segment names a BUILDING. Without these
        // guards it happily wrote "Bristol BS48 4NP" and bare village names
        // into the house-number field, which is worse than leaving it empty:
        // the customer submits a wrong door instead of being asked.
        const firstSeg = (searchInput.value || "").split(",")[0].trim();
        const lc = (v) => (v || "").trim().toLowerCase();
        // Check EVERY place-name component, not the first non-empty one — an
        // earlier `postal_town || locality` let "Warfield" (a locality, with
        // postal_town "Bracknell") through into the house-number field.
        const placeNames = ["postal_town", "locality", "administrative_area_level_1",
          "administrative_area_level_2", "sublocality_level_1", "country", "postal_code"]
          .map((t) => lc(val(t))).filter(Boolean);
        const looksPostcoded = /[A-Z]{1,2}[0-9][A-Z0-9]?\s*[0-9][A-Z]{2}/i.test(firstSeg);
        if (firstSeg && lc(firstSeg) !== lc(route) && !placeNames.includes(lc(firstSeg)) &&
            !looksPostcoded) {
          building = firstSeg;
        }
      }

      // No street on the place (rural named houses). Fall back only to a
      // sublocality — falling back to postal_town filled the street field with
      // the town in 9 of 74 audited addresses, so the customer saw the same
      // word twice and the carrier got no thoroughfare at all. Better to leave
      // it empty and let the "add the street or village" message do its job.
      const streetVal = route || val("sublocality_level_1") || "";
      const cityVal = val("postal_town") || val("locality") || val("administrative_area_level_2");
      const postcode = val("postal_code");
      // A sub-building (Flat 2) is extra info, not the building itself. So is a
      // named premise that lost to an actual street number — "Flat 2, Cranley
      // Mansions, 160 Gloucester Road" must not silently drop the mansion name.
      // mapPlaceLines in this same file already does this; keep them agreeing.
      const namedPremise = (!premiseIsNumber && premiseRaw && premiseRaw !== building) ? premiseRaw : "";
      const extra = [subpremise && subpremise !== building ? subpremise : "", namedPremise]
        .filter(Boolean).join(", ");

      // A picked address REPLACES the derived boxes. Guarding each assignment
      // let a stale value survive onto a different address — a prefilled "29"
      // plus a street-level pick of another road composed "29 <other road>",
      // a real house on a street the customer does not live on, silently.
      setDerived(buildingInput, "building", building);
      setDerived(streetInput, "street", streetVal);
      setDerived(city, "city", cityVal);
      setDerived(postcodeInput, "postcode", postcode);
      setDerived(line2, "extra", extra);

      // Report whether we SET a message, not whether it was an error — the
      // caller clears on a falsy return, so an informational message that
      // returned false was wiped in the same synchronous block, before paint.
      let messaged = false;
      if (!buildingInput.value.trim()) {
        setStatus("We couldn't work out the house number from that one. Please add it below.", "error");
        messaged = true;
      } else if (!streetInput.value.trim()) {
        // Informational, not an error: a named house in a hamlet genuinely has
        // no street, and the form accepts that.
        setStatus("That address has no street — the house name and town are enough. Add one if you have it.");
        messaged = true;
      }

      [buildingInput, streetInput, line2, city, postcodeInput].forEach((el) =>
        el.dispatchEvent(new Event("input", { bubbles: true })));
      if (typeof onApplied === "function") onApplied();
      return messaged;
    }

    searchInput.addEventListener("input", () => {
      const q = searchInput.value.trim();
      if (debounceTimer) clearTimeout(debounceTimer);
      if (q.length < 3) { clearList(); return; }
      if (q === lastQuery) return;
      lastQuery = q;
      debounceTimer = setTimeout(() => fetchSuggestions(q), 200);
    });
    searchInput.addEventListener("blur", () => setTimeout(clearList, 150));
    searchInput.addEventListener("keydown", (e) => {
      if (e.key === "Escape") { clearList(); return; }
      if (e.key !== "ArrowDown" && e.key !== "ArrowUp" && e.key !== "Enter") return;
      const items = listEl.querySelectorAll(".rw-addr-search__item");
      if (!items.length) return;
      const current = listEl.querySelector(".rw-addr-search__item--active");
      let idx = current ? Array.prototype.indexOf.call(items, current) : -1;
      if (e.key === "ArrowDown") { e.preventDefault(); idx = (idx + 1) % items.length; }
      if (e.key === "ArrowUp")   { e.preventDefault(); idx = (idx - 1 + items.length) % items.length; }
      if (e.key === "Enter" && current) {
        e.preventDefault();
        selectPlace(current.dataset.placeId, current.textContent);
        return;
      }
      items.forEach((el) => el.classList.remove("rw-addr-search__item--active"));
      if (items[idx]) items[idx].classList.add("rw-addr-search__item--active");
    });
  }

  /* ------------------------------ util ------------------------------ */
  function formatDate(iso) {
    const d = new Date(iso);
    return d.toLocaleDateString("en-GB", { weekday: "long", day: "numeric", month: "long" });
  }
  function escapeHtml(s) {
    return String(s || "")
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
      .replace(/'/g, "&#39;");
  }
  // Read claims from a HS256 JWT without verifying. Safe because the backend
  // re-verifies on every request — we only use this to decide which UI variant
  // to show (e.g. auto-default service type to "stylist" for partners).
  function decodeJwt(token) {
    if (!token || typeof token !== "string") return null;
    const parts = token.split(".");
    if (parts.length !== 3) return null;
    try {
      let mid = parts[1].replace(/-/g, "+").replace(/_/g, "/");
      mid += "=".repeat((4 - (mid.length % 4)) % 4);
      return JSON.parse(atob(mid));
    } catch { return null; }
  }

  function base64ToBlob(b64, type) {
    const bin = atob(b64);
    const bytes = new Uint8Array(bin.length);
    for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
    return new Blob([bytes], { type: type });
  }
  function downloadBlob(blob, filename) {
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  }

  // Lazy-load pdf-lib (≈300KB ESM) from the jsdelivr CDN and merge N PDFs into one.
  // Caches the module after first load so subsequent merges are instant.
  let _pdfLibPromise = null;
  function loadPdfLib() {
    if (!_pdfLibPromise) {
      _pdfLibPromise = import("https://cdn.jsdelivr.net/npm/pdf-lib@1.17.1/+esm");
    }
    return _pdfLibPromise;
  }
  async function mergePdfs(b64s) {
    const { PDFDocument } = await loadPdfLib();
    const out = await PDFDocument.create();
    for (const b64 of b64s) {
      const blob = base64ToBlob(b64, "application/pdf");
      const buf = await blob.arrayBuffer();
      const src = await PDFDocument.load(buf);
      const pages = await out.copyPages(src, src.getPageIndices());
      pages.forEach((p) => out.addPage(p));
    }
    const bytes = await out.save();
    return new Blob([bytes], { type: "application/pdf" });
  }

  window.__rwReturnsPortal = { state, api };

  boot();
})();
